<template>
    <div
        ref="photosContainer"
        tabindex="0"
        v-click-outside="handleClickOutsidePhotosContainer"
        @keydown="handleSlideKeydown"
        class="manage-photos-container"
        @wheel.passive="handleWheel"
        :style="{ '--max-slide-width': maxSlideWidth, '--slides-per-row': maxSlidesPerRow }"
    >
        <ActionBar
            ref="actionBar"
            :dropTarget="uploadDropTarget"
            :maxFilesLeft="maxFilesLeft"
            :isExpanded="isExpanded"
            :selectedSlides="selectedSlides"
            :highlightGroup="highlightGroup"
            :disableMoveBack="disableMoveBack"
            :disableMoveForward="disableMoveForward"
            :disableUploads="disableUploads"
            :disableRotate="selectedContainsVideo"
            :disableEdit="selectedContainsVideo"
            :read-only="$props.readOnly"
            :selectedUploadUsers="selectedUploadUsers"
            @download-filtered="handleDownloadFiltered"
            @download-selected="handleDownloadSelected"
            @download-all="handleDownloadAll"
            @add-text-slide="createTextSlide"
            @open-stock-media="openStockMedia"
            @temp-file-change="tempFiles => handleTempFilesChange(tempFiles)"
            @files-added="onFileAdded"
            @move-slide-backward="moveSlideBackward"
            @move-slide-forward="moveSlideForward"
            @edit-active-slide="editActiveSlide"
            @rotate-selected-slides="rotateSelectedSlides"
            @open-delete-modal="openDeleteModal"
            @successful-uploads="uploads => handleSuccessfulUploads(uploads)"
            @total-upload-progress="val => handleTotalUploadProgress(val)"
            @toggle-show-upload-users="showUploadUsers = !showUploadUsers"
            @init-filter-by-user="initFilterByUserModal"
            @toggle-order-mode-ui="setOrderModeUi(!orderModeUi)"
        >
            <template #right-buttons>
                <template v-if="!$props.readOnly">
                    <v-fade-transition>
                        <!-- @click="highlightAllDuplicates" -->
                        <v-chip
                            @click="openGroupResolveModal(groupTypes.DUPLICATE_PHASH)"
                            v-show="totalDuplicateSlides > 0"
                            color="#FEF3C7"
                            text-color="#92400E"
                        >
                            <div>
                                <font-awesome-icon
                                    class="mr-1"
                                    icon="fa-regular fa-triangle-exclamation"
                                ></font-awesome-icon>
                                <span> {{ totalDuplicateSlides }} Duplicates</span>
                            </div>
                        </v-chip>
                    </v-fade-transition>
                    <v-fade-transition>
                        <!-- @click="highlightAllSimilars" -->
                        <v-chip
                            @click="openGroupResolveModal(groupTypes.SIMILAR_LSH_ANY)"
                            v-show="totalSimilarSlides > 0"
                            color="#FEF3C7"
                            text-color="#92400E"
                        >
                            <div>
                                <font-awesome-icon
                                    class="mr-1"
                                    icon="fa-regular fa-triangle-exclamation"
                                ></font-awesome-icon>
                                <span> {{ totalSimilarSlides }} Similars </span>
                            </div>
                        </v-chip>
                    </v-fade-transition>

                    <template v-if="showDeadline && !state.forceExpandedWorkspace">
                        <TributeDeadlineChip />
                    </template>

                    <v-fade-transition>
                        <v-chip v-show="showDownloadProgress" color="#FEF3C7" text-color="#92400E">
                            <div>
                                <font-awesome-icon class="mr-1" icon="fa-regular fa-arrow-down"></font-awesome-icon>
                                Progress
                                {{ downloadProgressPercent }}%
                            </div>
                        </v-chip>
                    </v-fade-transition>

                    <v-fade-transition>
                        <v-chip v-show="showUploadProgress" color="#FEF3C7" text-color="#92400E">
                            <div>
                                <font-awesome-icon class="mr-1" icon="fa-regular fa-arrow-up"></font-awesome-icon>
                                Progress {{ uploadProgressPercent }}%
                            </div>
                        </v-chip>
                    </v-fade-transition>
                    <MaxItemsDisplay v-if="state.isContributor || state.isFamily" :uploads-count="myUploadsCount" />
                </template>
            </template>

            <template #bar-2-left>
                <div class="d-flex flex-wrap" style="gap: 12px">
                    <v-btn
                        @click="devMode = !devMode"
                        small
                        v-if="$auth.role.includes('SuperAdmin')"
                        depressed
                        :outlined="!devModeActive"
                        :dark="devModeActive"
                        >Dev Mode</v-btn
                    >
                    <div style="width: 140px; display: inline-flex; white-space: no-wrap">
                        <v-btn small icon @click="shrinkSlide" :disabled="slideWidth <= slideWidthMin">
                            <v-icon>mdi-minus</v-icon>
                        </v-btn>
                        <v-slider
                            color="#ff530d"
                            track-color="gray"
                            v-model="slideWidth"
                            :min="slideWidthMin"
                            :max="slideWidthMax"
                            :step="slideWidthStepSize"
                            dense
                            hide-details
                        ></v-slider>
                        <v-btn small icon @click="growSlide" :disabled="slideWidth >= slideWidthMax">
                            <v-icon>mdi-plus</v-icon>
                        </v-btn>
                    </div>
                    <v-btn @click="slideDisplayOriginalAspect = !slideDisplayOriginalAspect" icon>
                        <template v-if="slideDisplayOriginalAspect">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <g
                                    fill="none"
                                    stroke="#000"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                >
                                    <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                    <path d="M7 12V9h3m7 3v3h-3" />
                                </g>
                            </svg>
                        </template>
                        <template v-else>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path
                                    fill="#000"
                                    d="M19 4H5a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3M9 7a1 1 0 0 1 .117 1.993L9 9H7v2a1 1 0 0 1-.883.993L6 12a1 1 0 0 1-.993-.883L5 11V8a1 1 0 0 1 .883-.993L6 7zm9 5a1 1 0 0 1 .993.883L19 13v3a1 1 0 0 1-.883.993L18 17h-3a1 1 0 0 1-.117-1.993L15 15h2v-2a1 1 0 0 1 .883-.993z"
                                />
                            </svg>
                        </template>
                    </v-btn>

                    <!-- <v-chip
                        @click="deselectUploadUser(user)"
                        @click:close="deselectUploadUser(user)"
                        close
                        outlined
                        v-for="user in selectedUploadUsers"
                    >
                        {{ user.uploadUserName }}</v-chip
                    > -->
                </div>
            </template>

            <template #bar-2-center>
                <v-fade-transition v-if="!$props.readOnly">
                    <v-btn
                        depressed
                        v-show="highlightGroup.items"
                        @click="initResolveDuplicatesOrSimilars"
                        dark
                        small
                        color="error"
                    >
                        {{ resolveBtnText }}
                    </v-btn>
                </v-fade-transition>
                <v-fade-transition v-else>
                    <v-chip outlined> {{ totalSlides }} Slides </v-chip>
                </v-fade-transition>
                <div
                    v-if="!state.isMobile && !state.isContributor && !highlightGroup?.items?.length"
                    style="gap: 8px"
                    class="d-flex align-center"
                >
                    <v-chip
                        @click="quickFilterUser(user)"
                        v-for="(user, index) in uploadUsersDisplay.display"
                        :key="index"
                        label
                        :outlined="!userIsSelected(user)"
                        :color="user.color"
                        dark
                        link
                    >
                        {{ user.uploadUserName }}
                        <v-icon right v-if="userIsSelected(user)"> mdi-check-circle </v-icon>
                    </v-chip>
                    <v-menu offset-y open-on-hover v-if="uploadUsersDisplay.overflow.length">
                        <template v-slot:activator="{ on, attrs }">
                            <v-chip
                                outlined
                                color="purple"
                                label
                                v-bind="attrs"
                                v-on="on"
                                style="order: 999"
                                class="pointer"
                            >
                                <v-icon left> mdi-account-group </v-icon>
                                + {{ uploadUsersDisplay.overflow.length }}
                            </v-chip>
                        </template>
                        <v-list>
                            <v-list-item
                                v-for="(user, index) in uploadUsersDisplay.overflow"
                                :key="index"
                                @click="quickFilterUser(user)"
                            >
                                <v-list-item-title>
                                    <v-chip label link :outlined="!userIsSelected(user)" :color="user.color" dark>
                                        {{ user.uploadUserName }}
                                        <v-icon right v-if="userIsSelected(user)"> mdi-check-circle </v-icon>
                                    </v-chip>
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-btn
                        v-if="selectedUploadUsers.length"
                        style="order: 9999"
                        color="purple"
                        small
                        outlined
                        rounded
                        icon
                        @click="deselectAllUploadUsers"
                    >
                        <v-icon> mdi-close </v-icon>
                    </v-btn>
                </div>
            </template>

            <template #bar-2-right-prepend> </template>
        </ActionBar>

        <div ref="sortableSlides" :class="['slides-container', { expanded: isExpanded }]">
            <div
                class="placeholder slide"
                v-intersect="(entries, observer, isIntersecting) => handleSlidesStartIntersect(isIntersecting)"
                v-for="(item, index) in requiredPlaceholders"
                :key="index"
            >
                <v-skeleton-loader height="100%" type="image"></v-skeleton-loader>
            </div>
            <!-- <div
                v-if="showLoadMore"
                v-intersect="(entries, observer, isIntersecting) => handleSlidesStartIntersect(isIntersecting)"
                @click="() => slidesNextPage()"
                class="ignore-draggable"
            >
                <div class="d-flex flex-column align-center">
                    <v-progress-circular class="mb-4" size="50" color="orange" indeterminate></v-progress-circular>
                    <span class="text-caption">Loading slides..</span>
                </div>
            </div> -->

            <div
                v-for="(slide, index) in reversedSlides"
                :key="slide.id"
                :id="`slide-${slide.id}`"
                :guid="slide.guid"
                @click.prevent="handleSlideClick($event, slide)"
                @dblclick.prevent.stop="previewSlide($event, index, slide)"
                @contextmenu.prevent="initCustomContext($event, slide)"
                :class="[
                    'slide',

                    {
                        'ignore-draggable': slide?.loading || $props.readOnly || state.isContributor,
                        'show-upload-user': showUploadUsers,
                        'group-highlight': !$props.readOnly && inHighlightGroup(slide.id),
                        'cursor-disallowed': sortableDisabled,
                        'original-aspect': slideDisplayOriginalAspect,
                        'read-only': $props.readOnly,
                        'in-preview-highlight': shownInPreview(slide),
                    },
                ]"
            >
                <div v-if="devModeActive" class="devMode-overlay">
                    <div class="devMode-contents">
                        <div class="dev-mode-detail">index: {{ index }}</div>
                        <div class="dev-mode-detail">ID: {{ slide.id }}</div>
                        <div class="dev-mode-detail">Order: {{ slide.order }}</div>
                        <div class="dev-mode-detail">Upload Source: {{ uploadSources[slide.uploadSource].label }}</div>
                        <div class="dev-mode-detail">Upload Username: {{ slide.uploadUserName }}</div>
                        <div class="dev-mode-detail">Name: {{ slide.name }}</div>
                        <div class="dev-mode-detail">CreateDate: {{ slide.createDate }}</div>
                        <div class="dev-mode-detail">Mute: {{ slide.mute }}</div>
                        <div class="dev-mode-detail">Hash: {{ slide.perceptualHash }}</div>
                        <div class="dev-mode-detail">First Bits: {{ slide.lshFirstBits }}</div>
                        <div class="dev-mode-detail">MiddleBits: {{ slide.lshMiddleBits }}</div>
                        <div>Random Bits: {{ slide.lshRandomBits }}</div>
                        <div class="d-flex justify-center p-4">
                            <v-btn @click="openDevModeModal(slide)">Expand</v-btn>
                        </div>
                    </div>
                </div>
                <template v-if="selectedSlides.length > 1">
                    <div class="count-overlay" :class="[`chars-${selectedSlides.length.toString().length}`]">
                        {{ selectedSlides.length }}
                    </div>
                    <div class="count-overlay-text">{{ selectedSlides.length }} photos selected</div>
                </template>
                <div class="slide-top-left">
                    <div
                        class="slide-order"
                        :class="showUploadUsers ? '' : 'translate-up'"
                        v-if="!$props.readOnly || slide.mediaType === 1"
                        @click.prevent.stop="previewSlide($event, index, slide)"
                        :style="{
                            zIndex: 1000,
                        }"
                    >
                        <span v-if="!$props.readOnly">{{ getSlideDbIndex(index) }}</span>
                        <span v-if="slide.duration || slide.slideDuration" class="duration">
                            <font-awesome-icon v-if="slide.mediaType === 1" icon="fa-regular fa-video" />
                            <v-icon v-else class="custom-duration">mdi-timeline-clock-outline</v-icon>

                            <template v-if="!orderModeUi">
                                <template v-if="slide.slideDuration">
                                    {{ slide.slideDuration | formatTimeStamp }}
                                </template>
                                <template v-else="slide.duration">
                                    {{ slide.duration | formatTimeStamp }}
                                </template>
                            </template>
                        </span>
                    </div>

                    <CustomTooltip
                        :tooltipProps="{ top: true }"
                        v-if="
                            !$props.readOnly &&
                            !state.isContributor &&
                            isDuplicate(slide.perceptualHash) &&
                            !orderModeUi
                        "
                    >
                        <template #activator>
                            <div class="slide-order" @click.stop="highlightDuplicateGroupItems(slide.perceptualHash)">
                                <font-awesome-icon
                                    class="dupe-icon"
                                    icon="fa-solid fa-triangle-exclamation"
                                ></font-awesome-icon>
                            </div>
                        </template>

                        <template #title>
                            <span>Duplicates Found</span>
                        </template>
                        <template #content>
                            <span>Click to highlight duplicates</span>
                        </template>
                    </CustomTooltip>

                    <CustomTooltip
                        :tooltipProps="{ top: true }"
                        v-if="!$props.readOnly && isSimilar(slide) && !orderModeUi"
                    >
                        <template #activator>
                            <div class="slide-order" @click.stop="highlightSimilarGroupItems(slide)">
                                <font-awesome-icon
                                    class="dupe-icon"
                                    icon="fa-solid fa-triangle-exclamation"
                                ></font-awesome-icon>
                            </div>
                        </template>

                        <template #title>
                            <span>Similar Found</span>
                        </template>
                        <template #content>
                            <span>Click to highlight similar</span>
                        </template>
                    </CustomTooltip>
                </div>
                <CustomTooltip
                    v-if="!state.isContributor && !orderModeUi"
                    :class="['slide-user']"
                    :style="{ color: getUploadUserColor(slide) }"
                    :tooltipProps="{ top: true }"
                >
                    <template #activator>
                        <div class="slide-user-inner">
                            <font-awesome-icon icon="fa-solid fa-user"></font-awesome-icon>
                            <span>
                                {{ slide.uploadUserName | firstName }}
                            </span>
                        </div>
                    </template>
                    <template #content>
                        <strong class="mr-1">{{ slide.uploadUserName }}</strong>
                        <span v-if="slide.uploadUserRelationship !== slide.uploadUserName">{{
                            slide.uploadUserRelationship
                        }}</span>
                    </template>
                </CustomTooltip>

                <div class="slide-bottom-right">
                    <template v-if="!state.isMobile && !orderModeUi">
                        <CustomTooltip
                            v-if="!$props.readOnly && !inHighlightGroup(slide.id)"
                            class="slide-preview"
                            :tooltipProps="{ top: true }"
                        >
                            <template #activator>
                                <div @click.stop="selectAndDeleteSlide($event, slide)" class="slide-preview-inner">
                                    <button>
                                        <div>
                                            <font-awesome-icon style="color: #ff5252" icon="fa-regular fa-trash-can" />
                                        </div>
                                    </button>
                                </div>
                            </template>

                            <template #content>
                                <span>Delete</span>
                            </template>
                        </CustomTooltip>

                        <CustomTooltip class="slide-preview" :tooltipProps="{ top: true }">
                            <template #activator>
                                <div
                                    @click.stop="
                                        slide.mediaType === 1 || $props.readOnly
                                            ? previewSlide($event, index, slide)
                                            : editSlide($event, index, slide)
                                    "
                                    class="slide-preview-inner"
                                >
                                    <button>
                                        <div>
                                            <font-awesome-icon
                                                :icon="[
                                                    'fa-regular',
                                                    slide.mediaType === 1 || $props.readOnly ? 'fa-eye' : 'fa-pencil',
                                                ]"
                                            />
                                        </div>
                                    </button>
                                </div>
                            </template>

                            <template #content>
                                <span>{{ slide.mediaType === 1 || $props.readOnly ? 'Preview' : 'Edit' }}</span>
                            </template>
                        </CustomTooltip>
                    </template>
                    <template v-else-if="!orderModeUi">
                        <div @click.stop="openContextMenuMobile($event, slide)" class="slide-preview-inner">
                            <button>
                                <div>
                                    <font-awesome-icon icon="fa-regular fa-ellipsis-h" />
                                </div>
                            </button>
                        </div>
                    </template>
                </div>

                <div v-if="slide.mediaType === 1 && slide.mute" class="center-slide-overlay">
                    <font-awesome-icon
                        style="color: white; font-size: 2rem"
                        icon="fa-regular fa-volume-slash"
                    ></font-awesome-icon>
                </div>

                <div
                    v-if="inHighlightGroup(slide.id)"
                    @click.stop="selectAndDeleteSlide($event, slide)"
                    class="center-delete-btn"
                >
                    <font-awesome-icon
                        style="color: white; font-size: 1.8rem"
                        icon="fa-regular fa-trash-can"
                    ></font-awesome-icon>
                </div>

                <ImageThumbnail
                    :key="`${slide.id}-${slide.order}-${index}-${slide.rotation}`"
                    :src="slide.url"
                    preset="medium"
                    :type="slide.mediaType"
                    :alt="'Slide ' + getSlideDbIndex(index)"
                    class="slide-image"
                    :style="{
                        transform: 'rotate(' + slide.rotation + 'deg)',
                    }"
                    :retry-on-fail="slide.mediaType === 1"
                    @thumbnailLoaded="removeSlideLoadingState(slide)"
                    v-fancySwap="slide.loading"
                />

                <div
                    v-if="!$props.readOnly && selectedSlides.some(x => x.id === slide.id)"
                    class="selected-indicator"
                    :style="{ position: 'absolute', top: '8px', right: '8px', zIndex: 2 }"
                >
                    <svg
                        width="13.48"
                        height="10.5"
                        viewBox="0 0 13.48 10.5"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M1 4.33333L5.08021 8.41208L12.48 1" stroke="white" stroke-width="2" />
                    </svg>
                </div>

                <v-progress-circular
                    v-if="slide?.loading"
                    style="position: absolute; top: 0; bottom: 0; left: 0; right: 0; margin: auto; z-index: 2"
                    indeterminate
                    color="#e75a07"
                ></v-progress-circular>
            </div>

            <template v-for="(pendingSlide, index) in pendingUploads" v-if="pendingUploads.length">
                <div class="pending-slide ignore-draggable slide" :key="pendingSlide.id">
                    <div class="slide-top-left">
                        <div
                            class="slide-order"
                            :style="{
                                zIndex: 1000,
                            }"
                        >
                            {{ totalSlides + (index + 1) }}
                        </div>
                    </div>
                    <ImagePreview
                        class="slide-image"
                        :media="pendingSlide"
                        :alt="'Slide ' + totalSlides + (index + 1)"
                    />
                    <div class="progress-indicator">
                        <div class="progress-slider" :style="{ width: `${pendingSlide.progress.percentage}%` }"></div>
                    </div>
                </div>
            </template>
            <div
                v-if="totalSlides == 0 && !disableUploads"
                class="ghost-slide ignore-draggable slide"
                @click="triggerSlideUpload"
            >
                <div class="ghost-icon-container">
                    <svg
                        class="ghost-icon"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M3 17C3 16.4477 3.44772 16 4 16H16C16.5523 16 17 16.4477 17 17C17 17.5523 16.5523 18 16 18H4C3.44772 18 3 17.5523 3 17ZM6.29289 6.70711C5.90237 6.31658 5.90237 5.68342 6.29289 5.29289L9.29289 2.29289C9.48043 2.10536 9.73478 2 10 2C10.2652 2 10.5196 2.10536 10.7071 2.29289L13.7071 5.29289C14.0976 5.68342 14.0976 6.31658 13.7071 6.70711C13.3166 7.09763 12.6834 7.09763 12.2929 6.70711L11 5.41421L11 13C11 13.5523 10.5523 14 10 14C9.44771 14 9 13.5523 9 13L9 5.41421L7.70711 6.70711C7.31658 7.09763 6.68342 7.09763 6.29289 6.70711Z"
                            fill="#F97316"
                        />
                    </svg>
                </div>
            </div>

            <div v-for="i in mounted ? 0 : 10" class="slide ignore-draggable">
                <v-skeleton-loader height="100%" type="image"></v-skeleton-loader>
            </div>
        </div>

        <v-dialog v-model="showDownloadConfirmModal" max-width="500px">
            <v-card class="p-3">
                <v-card-title>
                    <div>
                        <span>
                            Download
                            {{ selectedSlides.length === 1 ? 'selected media' : `${selectedSlides.length} media` }}?
                        </span>
                    </div>
                </v-card-title>
                <v-card-text>
                    <div>
                        Confirm to download
                        {{
                            selectedSlides.length === 1
                                ? 'the selected media'
                                : `these ${selectedSlides.length} slides`
                        }}.
                    </div>
                </v-card-text>
                <v-card-actions class="d-flex justify-end">
                    <v-btn outlined @click="showDownloadConfirmModal = false">Cancel</v-btn>
                    <v-btn depressed color="#ff530d" dark @click="confirmDownloadSelected">Download</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showDeleteModal" max-width="500px">
            <v-card class="p-3">
                <v-card-title>
                    <div>
                        <span style="background-color: #fee2e1; border-radius: 10rem; padding: 6px">
                            <font-awesome-icon
                                style="color: #ff5254"
                                icon="fa-regular fa-triangle-exclamation"
                            ></font-awesome-icon>
                        </span>

                        <span>
                            Confirm deletion of
                            {{ selectedSlides.length }} media?
                        </span>
                    </div>
                </v-card-title>
                <v-card-text>
                    <div>
                        Are you sure you want to delete
                        {{ selectedSlides.length === 1 ? 'this slide' : `these ${selectedSlides.length} slides` }}? This
                        action cannot be undone.
                    </div>

                    <div class="modal-thumb-grid">
                        <!-- use cached gallery imgkit img, resize w/ css instead of setting smaller h/w dimensions -->
                        <ImageThumbnail
                            v-for="(slide, index) in selectedSlides"
                            :key="slide.url"
                            :src="slide.url"
                            :preset="'medium'"
                            :type="slide.mediaType"
                            :alt="'Slide ' + getSlideDbIndex(index)"
                            class="slide-image"
                        />
                    </div>
                </v-card-text>
                <v-card-actions class="d-flex justify-end">
                    <v-btn class="no-text-transform" outlined @click="closeDeleteModal">Cancel</v-btn>
                    <v-btn depressed color="error" class="btn btn-deactivate no-text-transform" @click="confirmDelete"
                        >Confirm</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- The Imgly CreativeEditor component wrapped in a modal -->
        <v-dialog v-model="showAddTextSlideModal" persistent>
            <CreativeEditor
                @onSave="onEditorSave"
                @close="showAddTextSlideModal = false"
                ref="imglyEditor"
                :eventId="$props.eventId"
                @loadNextSlide="currentSlideId => editAdjacentSlide(currentSlideId, 'next')"
                @loadPrevSlide="currentSlideId => editAdjacentSlide(currentSlideId, 'prev')"
                :funeralHomeId="tributeVideo.funeralHomeId"
                @export-progress="val => handleExportProgress(val)"
                @export-start="showEditorProgress = true"
                @export-cancelled="resetEditorProgress"
                @revertChanges="findSlideAndRevert"
            />
        </v-dialog>
        <!-- Slide preview modal -->
        <v-dialog
            v-model="showSlidePreviewModal"
            :overlay-opacity="0.85"
            :max-width="slidePreviewFullScreen ? null : '800px'"
            :fullscreen="slidePreviewFullScreen"
        >
            <SlidePreviewModal
                ref="previewCarousel"
                :showSlidePreviewModal="showSlidePreviewModal"
                @input="val => (showSlidePreviewModal = val)"
                :previewRefreshKey="previewRefreshKey"
                :reversedSlides="reversedSlides"
                :getSlideDbIndex="getSlideDbIndex"
                :getUploadUserColor="getUploadUserColor"
                :isVideoSlide="isVideoSlide"
                :handleVideoPreviewUrl="handleVideoPreviewUrl"
                :selectedContainsVideo="selectedContainsVideo"
                :videoEditingEnabled="videoEditingEnabled"
                @close="showSlidePreviewModal = false"
                :slidePreviewFullScreen="slidePreviewFullScreen"
                @toggleFullScreen="slidePreviewFullScreen = !slidePreviewFullScreen"
                @actionFromPreviewImage="actionFromPreviewImage"
                :startingIndex="previewingIndex"
                :devMode="devModeActive"
                @updatedPreviewIndex="
                    newIndex => {
                        handleNewPreviewIndex(newIndex);
                    }
                "
            />
        </v-dialog>
        <!-- </div> -->

        <v-dialog v-model="filterByUserModal" max-width="350px">
            <v-card>
                <v-card-title>Filter By Uploader</v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                    <v-list>
                        <v-list-item v-for="(user, index) in uploadUsers" :key="index">
                            <!-- <v-list-item-avatar>
                            <v-icon :class="file.color" dark v-text="file.icon"></v-icon>
                            test
                        </v-list-item-avatar> -->

                            <v-list-item-content>
                                <v-list-item-title v-text="user.uploadUserName"></v-list-item-title>

                                <v-list-item-subtitle v-text="user.uploadUserRelationship"></v-list-item-subtitle>
                            </v-list-item-content>

                            <v-list-item-action>
                                <v-checkbox @change="filterSlidesBySelectedUsers" v-model="user.selected"></v-checkbox>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </v-card-text>
                <v-card-actions class="d-flex justify-space-between">
                    <v-btn @click="closeUploadUserModal" class="no-text-transform" depressed outlined>Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <SlideGroupResolveModal
            v-if="selectedGroupType != null && !state.isContributor"
            @stage-delete="slides => stageForDelete(slides)"
            v-model="groupResolveModal"
            :groups="selectedGroups"
            :groupType="selectedGroupType"
        />

        <StockMediaModal
            @init-import="(file, editAfterUpload) => handlePexelImport(file, editAfterUpload)"
            @edit-media="(file, mediaType) => handleEditStockPhoto(file, mediaType)"
            v-model="showStockMediaModal"
        />

        <v-dialog v-model="devModeModal" v-if="devModeActive" max-width="700px">
            <v-card>
                <v-card-text style="max-height: 700px; overflow: auto">
                    <div style="margin: 24px 0">
                        <div v-if="devModeSelectedSlide">
                            Upload Source: {{ uploadSources[devModeSelectedSlide.uploadSource].label }}
                        </div>
                        <div style="border-radius: 4px; background-color: #f1f4f7; margin: 24px 0">
                            <pre>Raw:</pre>
                            <pre class="dev-mode-modal-content">{{ devModeSelectedSlide }}</pre>
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-btn depressed @click="closeDevModeModal">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showEditorProgress" max-width="400px">
            <v-card>
                <v-card-title>
                    <span v-if="editorProgressStage === 'upload'">Uploading..</span>
                    <span v-else>Rendering..</span>
                </v-card-title>

                <v-card-text>
                    <v-progress-linear
                        :color="editorUploadProgress === 100 ? 'success' : 'primary'"
                        dark
                        height="20"
                        v-if="editorProgressStage === 'upload'"
                        v-model="editorUploadProgress"
                    >
                        {{ editorUploadProgress }}%
                    </v-progress-linear>
                    <v-progress-linear dark height="20" v-else v-model="editorExportProgress">
                        {{ editorExportProgress }}%
                    </v-progress-linear>
                </v-card-text>

                <v-card-actions>
                    <v-btn
                        v-if="editorProgressStage === 'export'"
                        depressed
                        class="no-text-transform"
                        @click="cancelEditorExport"
                        >Cancel</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="moveToPositionModal" v-if="moveToPositionModal && selectedSlides" max-width="420px">
            <v-card class="keep-selection">
                <v-card-title>Move {{ selectedSlides.length === 1 ? 'Slide' : 'Slides' }} To Position</v-card-title>
                <v-card-text class="pt-2">
                    <p v-if="selectedSlides.length > 1">{{ selectedSlides.length }} slides will be moved</p>
                    <div class="modal-thumb-grid" :class="{ single: selectedSlides.length === 1 }">
                        <div class="thumb" v-for="(slide, index) in selectedSlides">
                            <span class="slide-order">
                                {{ slide.order + 1 }}
                            </span>
                            <ImageThumbnail
                                :key="slide.url"
                                :src="slide.url"
                                :preset="'medium'"
                                :type="slide.mediaType"
                                class="slide-image"
                            />
                        </div>
                    </div>
                </v-card-text>
                <v-card-text>
                    <p>Choose desired position between 1 and {{ totalSlides }}.</p>
                    <v-text-field
                        label="Desired Position"
                        outlined
                        hide-details
                        v-model.number="moveToPositionIndex"
                        :min="1"
                        step="1"
                        @focus="$event => $event.target.select()"
                        @blur="
                            () => {
                                if (moveToPositionIndex < 1) {
                                    moveToPositionIndex = 1;
                                }
                                if (moveToPositionIndex > totalSlides) {
                                    moveToPositionIndex = totalSlides;
                                }
                            }
                        "
                        :max="totalSlides"
                        type="number"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn depressed @click="moveToPositionModal = false">Close</v-btn>
                    <v-spacer />
                    <v-btn depressed @click="moveToPosition" color="green"
                        >Move {{ selectedSlides.length === 1 ? 'Slide' : 'Slides' }}</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
        <custom-context-menu ref="customContext" id="customContext" :items="contextItems" top></custom-context-menu>
    </div>
</template>

<script>
import sortableHelper from '@/utilities/sortableHelper';
import multiSelectUtils from '../../utilities/multiSelectUtils';
import draggable from 'vuedraggable';
import modal from './WarningModal.vue';
import initApiServices from '@/services/ApiInitializer';
import { debounce, each, map, flatMap, partition, keyBy, findIndex } from 'lodash';
import JSZip from 'jszip';

import ImagePreview from '../Media/ImagePreview.vue';
import ImageThumbnail from '../Media/ImageThumbnail.vue';
import { mapActions } from 'pinia';
import ActionBar from './Slides/ActionBar.vue';

import CustomTooltip from '@/components/ui/CustomTooltip.vue';
import CustomContextMenu from '@/components/ui/CustomContextMenu.vue';
import SlideGroupResolveModal from './Modals/SlideGroupResolveModal.vue';
import StockMediaModal from '@/components/Tribute/Modals/StockMediaModal.vue';
import StyledVideojsPlayer from '@/components/videojs/StyledVideojsPlayer.vue';
import SlidePreviewModal from './Modals/SlidePreviewModal.vue';

import { slideGroupTypes, uploadSources, tributePerformanceModeEnum } from '@/constants';
import moment from 'moment';
import { withErrorReporting } from '@/utilities/bugsnag-helper';
import { getTributeUploadSessionId, setTributePerformanceMode } from '@/utilities/session';

import TributeDeadlineChip from '@/components/Tribute/TributeDeadlineChip.vue';
import MaxItemsDisplay from './MaxItemsDisplay.vue';
import { useTributeVideoStore } from '@/stores/tributeVideo';
import { useTributeEditorStore } from '@/stores/tributeEditor';
import { useGlobalState } from '@/stores/globalState';

const ENABLE_VIDEO_EDITING = true;

export default {
    name: 'ManagePhotos',
    setup() {
        return {
            tributeVideo: useTributeVideoStore(),
            tributeEditor: useTributeEditorStore(),
        };
    },
    components: {
        TributeDeadlineChip,
        draggable,
        modal,
        ImagePreview,
        ImageThumbnail,
        ActionBar,
        CustomTooltip,
        CustomContextMenu,
        SlideGroupResolveModal,
        StockMediaModal,
        StyledVideojsPlayer,
        MaxItemsDisplay,
        SlidePreviewModal,
        // PhotoEditor: () => import('@/components/ui/PhotoEditor'),
        CreativeEditor: () => import('./Editor/CreativeEditor.vue'),
    },
    inject: ['state'],
    props: {
        eventId: {
            type: Number,
            required: true,
        },
        tributeVideo: {
            type: Object,
            required: true,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        windowResizeCounter: {
            type: Number,
            default: 0,
        },
        previewCurrentTime: {
            type: Number,
            default: 0,
        },
    },
    created() {
        this.debouncedSocketRefresh = debounce(this.handleSocketRefresh, 500);
        this.debouncedMultiSlideRotate = debounce(this.initMultiSlideRotate, 700);

        this.debouncedNextPage = debounce(this.slidesNextPage, 100);
    },
    beforeDestroy() {
        this.debouncedMultiSlideRotate?.cancel?.();
        this.debouncedSocketRefresh?.cancel?.();
        this.debouncedNextPage?.cancel();
    },
    async mounted() {
        this.setContextItems();

        if (this.state.token) {
            this.token = this.state.token;
        } else {
            const response = await this.$auth.getIdTokenClaims();
            this.token = response.__raw;
        }
        this.apiService = initApiServices(this.token);

        if (this.eventId) {
            let data = {
                reversed: true,
                pageNumber: this.pageNumber,
                pageSize: this.pageSize,
            };

            this.replaceCurrentPhotos(data);
        }

        if (this.tributeVideo.serviceId) {
            if (this.dueForADupeCheck) {
                this.refreshDuplicateAndSimilarSlides(this.tributeVideo.serviceId);
            } else {
                // process existing similar and duplicates data since we don't need to fetch it for now
                this.processDupesAndSimilars({
                    dupes: this.tributeVideo.duplicateSlideGroups,
                    similars: this.tributeVideo.similarSlideGroups,
                });
            }
        }

        this.initSortable();

        if (this.tributeVideo.id) {
            this.getUploadUsers(this.tributeVideo.id);
        }
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.handlePreviewModalKeydown);
    },
    data() {
        return {
            tributePerformanceModeEnum,
            apiService: null,
            token: null,
            pendingUploads: [],
            reversedSlides: [],
            selectedSlides: [],
            totalSlides: 0,
            isDragging: false,
            dragIndex: null,
            sortableInstance: null,
            showAddTextSlideModal: false,
            previewingIndex: -1,
            showSlidePreviewModal: false,
            slidePreviewFullScreen: false,
            textSlideBgColor: '#ffffff',
            textSlideContent: '',
            showDeleteModal: false,
            recordedScrollTop: 0,
            recordedScrollLeft: 0,
            pageNumber: 0,
            pageSize: 24,
            mounted: false,
            progress: 0,
            pendingReorder: false,
            pendingDelete: false,
            pendingRotate: false,
            selectRangeStart: null,
            uploadDropTarget: '.manage-photos-container',
            fetchingSlides: false,
            fetchingAllSlides: false,
            uploadUsers: [],
            devMode: false,
            devModeSelectedSlide: null,
            devModeModal: false,
            moveToPositionModal: false,
            moveToPositionIndex: -1,
            imglyConfig: {
                theme: 'light',
                mobile: this.state.isMobile,
                // layout: 'advanced',
                // layout: 'basic',
                license: '{"owner":"Imgly Inc.","version":"2.4"}',
            },
            uploadSources,
            editingPhoto: null,
            downloadProgressMap: {},
            downloadProgressPercent: 0,
            showDownloadProgress: false,
            showDownloadConfirmModal: false,
            showUploadProgress: false,
            uploadProgressPercent: 0,
            showUploadUsers: true,
            filterByUserModal: false,
            contextItems: [],
            contextRefreshKey: 0,
            previewRefreshKey: 0,
            duplicateSlideGroups: {
                results: [],
                total: 0,
            },
            similarSlideGroups: {
                results: [],
                total: 0,
            },
            totalDuplicateSlides: 0,
            totalSimilarSlides: 0,
            highlightGroup: {
                keys: null,
                groupType: null,
                items: null,
            },
            groupResolveModal: false,
            groupTypes: slideGroupTypes,
            selectedGroups: [],
            selectedGroupType: null,
            showStockMediaModal: false,
            currentRotation: 0,
            currentScrollPercentage: 0,
            sortableDisabled: false,
            defaultSlideWidth: 242,
            slideWidth: 242,
            slideWidthMin: 110,
            slideWidthMax: 430,
            slideWidthStepSize: 40,
            cachedSlideWidth: 242,
            cacheSlideWidth: true,
            slideDisplayOriginalAspect: false,
            videoEditingEnabled: ENABLE_VIDEO_EDITING,
            showEditorProgress: false,
            editorExportProgress: 0,
            editorUploadProgress: 0,
            editorProgressStage: 'export',
            saveInProgress: null,
            orderModeUi: false,
        };
    },
    computed: {
        performanceMode() {
            return this.tributeVideo.performanceMode;
        },
        requiredPlaceholders() {
            const reqSlideCount = this.totalSlides - this.reversedSlides.length;
            return reqSlideCount >= 0 ? reqSlideCount : 0;
        },
        selectedContainsVideo() {
            return this.selectedSlides.some(s => s.mediaType === 1);
        },
        devModeActive() {
            return this.devMode && this.$auth?.role?.includes('SuperAdmin');
        },
        maxFiles() {
            if (this.state.isContributor) {
                return this.tributeVideo.maxSessionItems || this.tributeVideo.maxItems;
            }
            return this.tributeVideo.maxItems;
        },
        maxFilesLeft() {
            // computed difference between how many files max and existing items
            // TODO: included pending uploads while they resolve and remove if they error, etc
            return this.maxFiles - this.myUploadsCount;
        },
        isExpanded() {
            return this.tributeEditor.isExpanded;
        },
        containerHeight() {
            return this.tributeEditor.computedWorkspaceHeight;
        },
        isAnyImageSelected() {
            return this.selectedSlides.length > 0;
        },

        disableMoveBack() {
            if (this.$props.readOnly) return true;

            if (this.selectedSlides.length !== 1) return true;

            if (this.reversedSlides.length === 0) return true;

            return this.reversedSlides?.[0]?.id === this.selectedSlides?.[0]?.id;
        },

        disableMoveForward() {
            if (this.$props.readOnly) return true;
            if (this.selectedSlides.length !== 1) return true;

            if (this.reversedSlides.length === 0) return true;

            return this.reversedSlides[this.reversedSlides.length - 1].id === this.selectedSlides[0].id;
        },
        disableUploads() {
            if (this.$props.readOnly) return true;
            // Add logic here to disable if needed

            if (this.maxFilesLeft <= 0) return true;
            return false;
        },
        showLoadMore() {
            return this.reversedSlides.length < this.totalSlides;
        },
        isFamilyPage() {
            return this.$route.name === 'TributeVideoFamily';
        },
        isContributorPage() {
            return this.$route.name === 'TributeVideoContributor';
        },
        resolveBtnText() {
            let text = 'Resolve';
            if (this.highlightGroup?.groupType === this.groupTypes.SIMILAR_LSH_ANY) {
                text += ' Similar';
            } else if (this.highlightGroup?.groupType === this.groupTypes.DUPLICATE_PHASH) {
                text += ' Duplicates';
            }
            return text;
        },
        selectedUploadUsers() {
            const selectedUsers = this.uploadUsers
                .filter(x => x.selected)
                .map(user => {
                    return {
                        uploadUserName: user.uploadUserName,
                        uploadUserRelationship: user.uploadUserRelationship,
                    };
                });

            return selectedUsers;
        },
        myUploadsCount() {
            if (this.state?.anonUserDetails) {
                return this.reversedSlides.filter(
                    slide =>
                        slide.uploadUserName === this.state.anonUserDetails.name &&
                        slide.uploadUserRelationship === this.state.anonUserDetails.relationship,
                ).length;
            }
            if (this.$auth?.user?.name) {
                return this.reversedSlides.filter(slide => slide.uploadUserName === this.$auth.user.name).length;
            }
            return 0;
        },
        showDeadline() {
            if (this.$props.readOnly) return true;

            return (
                !this.state.isMobile &&
                this.tributeVideo.deadline &&
                (this.state.isContributor || this.state.isFamily) &&
                !(this.showDownloadProgress || this.showUploadProgress)
            );
        },
        maxSlideWidth() {
            // console.log('calculating max Slide Width', { slideWidth: this.slideWidth, windowInnerWidth: window.innerWidth });
            const maxSlidePx = window.innerWidth / this.maxSlidesPerRow;
            const maxSlidesPercentage = Math.floor((maxSlidePx / window.innerWidth) * 100);
            return `${maxSlidesPercentage}vw`;
        },
        maxSlidesPerRow() {
            if (this.state.isMobile) {
                return 2;
            }
            return Math.floor(window.innerWidth / this.slideWidth);
        },
        dueForADupeCheck() {
            return this.tributeVideo.dueForADupeCheck;
        },
        dupesAndSims() {
            return this.tributeVideo.duplicateSlideGroups.total + this.tributeVideo.similarSlideGroups.total;
        },
        uploadUsersDisplay() {
            // reference resize counter to trigger refreshing on resize
            this.$props.windowResizeCounter;

            // Payload is an object with two arrays, display, and overflow
            // Also ensure that selected filtered user is up in front
            const partitions = partition(this.uploadUsers, 'selected');
            const filteredUsers = [...partitions[0], ...partitions[1]];
            let displaySlice = 2;

            const w = window.innerWidth;
            if (w > 1920) {
                displaySlice = 5;
            } else if (w >= 1180 && w <= 1920) {
                displaySlice = 4;
                // } else if (w < 1000) {
                //     displaySlice = 2;
            }
            return {
                display: filteredUsers.slice(0, displaySlice),
                overflow: filteredUsers.slice(displaySlice),
            };
        },
    },
    filters: {
        formatTimeStamp(seconds) {
            const duration = moment.duration(seconds, 'seconds');
            const hours = duration.hours();
            const minutes = duration.minutes();
            const secs = duration.seconds();

            const formattedMinutes = String(minutes).padStart(2, '0');
            const formattedSeconds = String(secs).padStart(2, '0');
            if (hours > 0) {
                return `${hours}:${formattedMinutes}:${formattedSeconds}`;
            }
            return `${formattedMinutes}:${formattedSeconds}`;
        },
        initials(text) {
            const words = text?.split(' ').filter((_word, index, arr) => index === 0 || index === arr.length - 1);
            return map(words, w => w.charAt(0)).join('');
        },
        firstName(text) {
            const words = text?.split(' ');

            if (words.length > 0) return words[0];

            return null;
        },
    },
    watch: {
        pendingReorder(newVal) {
            this.setSortableDisabled(newVal);
        },
        isExpanded(newVal, oldVal) {
            if (newVal) {
                this.$refs.photosContainer.focus();
            }

            this.recordScrollPercentage(oldVal);

            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    this.restoreScrollPercentage(newVal);
                });
            });
        },
        selectedSlides(newVal) {
            const selectedIds = newVal.map(slide => slide.id);
            this.syncFromSelected(selectedIds);
            this.setContextItems();
        },
        previewingIndex(newIndex, oldIndex) {
            if (newIndex >= 0) {
                this.selectedSlides = [this.reversedSlides[newIndex]];
                if (this.selectedSlides.length) {
                    const selectedIds = this.selectedSlides.map(slide => slide?.id);
                    this.syncFromSelected(selectedIds);
                }
            }
        },
        devModeModal(newVal) {
            if (!newVal) {
                this.closeDevModeModal();
            }
        },
        showSlidePreviewModal(val) {
            if (val) {
                window.addEventListener('keydown', this.handlePreviewModalKeydown);
            } else {
                window.removeEventListener('keydown', this.handlePreviewModalKeydown);

                if (this.$refs.previewCarousel) {
                    const players = Array.isArray(this.$refs.previewCarousel?.$refs?.videoPlayer)
                        ? this.$refs.previewCarousel?.$refs?.videoPlayer
                        : [this.$refs.previewCarousel?.$refs?.videoPlayer];

                    players.forEach(el => {
                        if (el && typeof el.pause === 'function') {
                            el.pause();
                        }
                    });
                }
            }
        },
        dupesAndSims(newVal) {
            this.processDupesAndSimilars({
                dupes: this.tributeVideo.duplicateSlideGroups,
                similars: this.tributeVideo.similarSlideGroups,
            });
        },
        selectedUploadUsers(val) {
            if (val.length === 0) {
                this.setSortableDisabled(false);
            } else {
                this.setSortableDisabled(true);
            }
        },
        slideWidth(val) {
            if (this.cacheSlideWidth) {
                this.cachedSlideWidth = val;
            }
        },
        async orderModeUi(val) {
            this.cacheSlideWidth = false;

            if (val) {
                this.slideWidth = this.slideWidthMin;
            } else {
                this.slideWidth = this.cachedSlideWidth;
            }

            await this.$nextTick();
            this.cacheSlideWidth = true;
        },
    },
    methods: {
        ...mapActions(useGlobalState, ['showSnackbar', 'block']),
        // ...mapActions('tributeEditor', ['toggleExpandedState', 'setExpandedState']),
        async handleNewPreviewIndex(newIndex) {
            if (newIndex === 0 && this.totalSlides > this.reversedSlides.length) {
                let car = this.$refs.previewCarousel;

                car?.block(true);
                await this.fetchAllSlides();
                car?.block(false);
            }

            this.previewingIndex = newIndex;
        },
        shownInPreview(slide) {
            if (this.performanceMode === tributePerformanceModeEnum.PERFORMANCE) return false;
            if (!slide.slideStartMs && !slide.slideEndMs) return false;

            const currentMs = this.previewCurrentTime * 1000;
            if (currentMs >= slide.slideStartMs && currentMs <= slide.slideEndMs) {
                return true;
            }

            return false;
        },
        handleClickOutsidePhotosContainer(evt) {
            const contextMenu = document.getElementById('context-menu');

            if (contextMenu && contextMenu.contains(evt.target)) {
                console.log('click was inside context menu');
                return;
            } else if (evt.target.closest('.keep-selection')) {
                console.log('click was inside of a .keep-selection');
                return;
            } else {
                this.deselectAllSlides();
            }
        },
        handleVideoPreviewUrl(url) {
            let imgKitUrl = url.replace(
                'https://videos-dev.memoryshare.com/tribute-photos/',
                process.env.VUE_APP_IMG_KIT_BASE,
            );

            const rtMatch = url.match(/rt-(\d+)/);
            const rotationParam = rtMatch ? `,rt-${rtMatch[1]}` : '';

            imgKitUrl = imgKitUrl.split('?')[0] += `?tr=q-50${rotationParam}`;

            // console.log('HANDLE VIDEO URL PREVIEW', imgKitUrl);

            return imgKitUrl;
        },
        handleExportProgress(val) {
            if (val > this.editorExportProgress) {
                this.editorExportProgress = val;
            }
        },
        resetEditorProgress() {
            this.showEditorProgress = false;

            setTimeout(() => {
                this.editorProgressStage = 'export';
                this.editorExportProgress = 0;
                this.editorUploadProgress = 0;
            }, 200);
        },
        async cancelEditorExport() {
            if (this.editorProgressStage !== 'export') return;

            const editor = this.$refs.imglyEditor;
            if (!editor) return;

            editor.cancelExport();
        },
        openDevModeModal(slide) {
            this.devModeSelectedSlide = slide;

            this.devModeModal = true;
        },
        closeDevModeModal() {
            this.devModeModal = false;
            this.devModeSelectedSlide = null;
        },
        handlePexelImport(file) {
            try {
                const uploader = this.$refs.actionBar.$refs.uploaderBtn;

                if (uploader) {
                    uploader.addFile(file);
                }
            } catch (error) {
                console.log('error during import', error);
            }
        },
        async handleEditStockPhoto(file, mediaType) {
            console.log('handleEditStockPhoto', { file, mediaType });

            this.addNewTextSlide();
            // const waitingOnEditor = setInterval(() => {
            //     // console.log('Checking if editor is ready...');
            //     // if (this.$refs?.imgly)
            //     if (this.$refs?.photoEditor?.editor) {
            //         const photoEditor = this.$refs.photoEditor;
            //         photoEditor.editor.setImage(file);
            //         clearInterval(waitingOnEditor);
            //         // console.log('Editor is ready!');
            //     }
            // }, 200);
            await this.$nextTick();
            if (this.$refs?.imglyEditor) {
                const photoEditor = this.$refs.imglyEditor;

                photoEditor.createSceneFromURL(file, null, mediaType, {
                    slide: null,
                    prevSlide: this.reversedSlides[this.reversedSlides.length - 1],
                    nextSlide: null,
                });
            }
        },
        openStockMedia() {
            this.showStockMediaModal = true;
        },
        getUploadUserColor(slide) {
            let uploadUser = this.uploadUsers.find(
                x =>
                    x.uploadUserName === slide.uploadUserName &&
                    x.uploadUserRelationship === slide.uploadUserRelationship,
            );

            if (uploadUser) {
                return uploadUser?.color;
            }
        },
        getUserColor(i) {
            const colors = ['#ff530d', '#3f51b5', '#009688', '#309df4'];

            return colors[i % colors.length];
        },
        userIsSelected(user) {
            var results = this.selectedUploadUsers.filter(
                x =>
                    x.uploadUserName === user.uploadUserName &&
                    x.uploadUserRelationship === user.uploadUserRelationship,
            );

            return results.length > 0;
        },
        quickFilterUser(user) {
            if (this.userIsSelected(user)) {
                this.deselectUploadUser(user);
                return;
            }

            this.uploadUsers = this.uploadUsers.map(u => {
                let selected = this.userIsSelected(u);
                if (
                    u.uploadUserName === user.uploadUserName &&
                    u.uploadUserRelationship === user.uploadUserRelationship
                ) {
                    selected = true;
                }

                return { ...u, selected };
            });

            this.filterSlidesBySelectedUsers();
        },
        stageForDelete(slides) {
            this.selectedSlides = slides;
            const selectedIds = this.selectedSlides.map(slide => slide.id);
            this.syncFromSelected(selectedIds);
            this.openDeleteModal();
        },
        openGroupResolveModal(groupType) {
            // groupTypes.DUPLICATE_PHASH

            // this.highlightAllDuplicates();
            // console.log('opening resolve modal');

            let group = [];
            switch (groupType) {
                case this.groupTypes.DUPLICATE_PHASH:
                    group = this.duplicateSlideGroups?.results;
                    break;
                case this.groupTypes.SIMILAR_LSH_ANY:
                    group = this.similarSlideGroups?.results;
                    break;
                default:
                    console.log('Unsupported group type');
                    return;
            }
            this.selectedGroupType = groupType;
            this.selectedGroups = group || [];

            this.groupResolveModal = true;
        },
        deselectAllUploadUsers() {
            each(this.uploadUsers, user => {
                user.selected = false;
            });
            this.filterSlidesBySelectedUsers();
        },
        deselectUploadUser(user) {
            const found = this.uploadUsers.find(
                x =>
                    x.uploadUserName === user.uploadUserName && x.uploadUserRelationship == user.uploadUserRelationship,
            );

            if (found) {
                found.selected = false;
            }

            this.filterSlidesBySelectedUsers();
        },
        async filterSlidesBySelectedUsers(pageSize = null) {
            this.pageNumber = 0;
            if (pageSize == null) {
                this.pageSize = Math.max(24, this.reversedSlides.length + 1);
            } else {
                this.pageSize = pageSize;
            }

            let data = {
                reversed: true,
                pageNumber: this.pageNumber,
                pageSize: this.pageSize,
            };

            if (this.selectedUploadUsers.length) {
                data.uploadUsers = this.selectedUploadUsers;
            }

            await this.replaceCurrentPhotos(data);
        },
        closeUploadUserModal() {
            this.filterByUserModal = false;
        },
        setOrderModeUi(val) {
            this.orderModeUi = val;
        },
        initFilterByUserModal() {
            this.filterByUserModal = true;

            this.getUploadUsers(this.tributeVideo.id);
        },
        async getUploadUsers(tributeId) {
            try {
                if (!tributeId) throw new Error('Invalid tribute id');

                const resp = await this.apiService.tributePhoto.getUploadUsers(this.tributeVideo.id);

                if (resp.data) {
                    const currentSelections = new Set(
                        this.uploadUsers
                            .filter(x => x.selected)
                            .map(x => `${x.uploadUserName}::${x.uploadUserRelationship}`),
                    );

                    this.uploadUsers = resp.data.map((user, i) => {
                        const key = `${user.uploadUserName}::${user.uploadUserRelationship}`;

                        return {
                            ...user,
                            color: this.getUserColor(i),
                            selected: currentSelections.has(key),
                        };
                    });
                }
            } catch (error) {}
        },
        getUploaderLabelColor(slide) {
            if (slide.uploadUserRelationship === 'Funeral Staff') {
                return 'gray-label';
            }

            return 'orange-label';
        },
        initResolveDuplicatesOrSimilars() {
            if (!this.highlightGroup.keys) return;

            let flaggedItems = [];
            switch (this.highlightGroup.groupType) {
                case this.groupTypes.DUPLICATE_PHASH:
                    flaggedItems = this.duplicateSlideGroups?.results;
                    break;
                case this.groupTypes.SIMILAR_LSH_ANY:
                    flaggedItems = this.similarSlideGroups?.results;
            }

            if (flaggedItems.length === 0) return;

            let slidesToDelete = [];
            for (const index in this.highlightGroup.keys) {
                const key = this.highlightGroup.keys[index];

                const group = this.getGroupByGroupKey(flaggedItems, key);

                if (!group?.photos.length > 1) continue;

                const nonOriginalDuplicates = group.photos.slice(1);
                slidesToDelete = slidesToDelete.concat(nonOriginalDuplicates);
            }

            this.resetHighlightGroup();
            this.selectedSlides = slidesToDelete;
            const selectedIds = this.selectedSlides.map(slide => slide.id);
            this.syncFromSelected(selectedIds);
            this.openDeleteModal();
        },

        selectAndDeleteSlide(event, slide) {
            this.deselectAllSlides();
            this.handleSlideClick(event, slide);
            this.openDeleteModal();
        },
        openContextMenuMobile(event, slide) {
            const customContext = this.$refs.customContext;
            if (!customContext) return;
            // This tells outsideClickHandler to ignore the first click
            // otherwise it auto-closes.
            if (document.querySelector('#context-menu.hidden')) {
                event._skipClickOutsideHandler = true;
            }
            this.deselectAllSlides();
            this.handleSlideClick(event, slide);
            customContext.handleCustomContext(event, slide);
        },
        getGroupByGroupKey(arr, key) {
            if (!key) return null;

            if (!Array.isArray(arr)) return null;

            const found = arr.find(x => x.groupKey === key);

            return found || null;
        },
        inHighlightGroup(id) {
            if (!Array.isArray(this.highlightGroup?.items)) return false;

            const found = this.highlightGroup?.items.find(x => x.id === id);

            return !!found;
        },
        async highlightAllDuplicates() {
            this.deselectAllSlides();
            await this.fetchAllSlides();

            this.tributeEditor.setExpandedState(true);
            const arr = this.duplicateSlideGroups?.results;

            if (!arr) return;

            let photoGroup = [];
            let keys = [];

            for (const index in arr) {
                const group = arr[index];
                const photos = group?.photos;
                const groupKey = group?.groupKey;

                if (photos) {
                    photoGroup = photoGroup.concat(photos);

                    keys.push(groupKey);
                }
            }

            this.highlightGroup = {
                keys: keys,
                groupType: this.groupTypes.DUPLICATE_PHASH,
                items: photoGroup,
            };

            this.scrollToFirstInHighlightGroup();
        },
        async highlightAllSimilars() {
            this.deselectAllSlides();
            await this.fetchAllSlides();

            this.tributeEditor.setExpandedState(true);
            const arr = this.similarSlideGroups?.results;

            if (!arr) return;

            let groupPhotos = [];
            let keys = [];
            for (const index in arr) {
                const group = arr[index];
                const photos = group?.photos;
                const groupKey = group?.groupKey;

                if (photos) {
                    groupPhotos = groupPhotos.concat(photos);
                    keys.push(groupKey);
                }
            }

            this.highlightGroup = {
                groupType: this.groupTypes.SIMILAR_LSH_ANY,
                items: groupPhotos,
                keys: keys,
            };

            this.scrollToFirstInHighlightGroup();
        },
        scrollToFirstInHighlightGroup() {
            if (!Array.isArray(this.highlightGroup?.items)) return;

            for (let i = 0; i <= this.reversedSlides.length - 1; i++) {
                const slide = this.reversedSlides[i];

                if (this.highlightGroup?.items.some(item => item.id === slide.id)) {
                    this.focusImage(i);
                    break;
                }
            }
        },
        highlightDuplicateGroupItems(groupKey) {
            this.deselectAllSlides();
            this.tributeEditor.setExpandedState(true);

            const group = this.getGroupByGroupKey(this.duplicateSlideGroups?.results, groupKey);

            if (group?.photos) {
                this.highlightGroup = {
                    keys: [groupKey],
                    groupType: this.groupTypes.DUPLICATE_PHASH,
                    items: group.photos,
                };

                this.scrollToFirstInHighlightGroup();
            } else {
                this.resetHighlightGroup();
            }
        },
        resetHighlightGroup() {
            this.highlightGroup = {
                keys: null,
                groupType: null,
                items: null,
            };
        },
        highlightSimilarGroupItems(slide) {
            this.deselectAllSlides();
            this.tributeEditor.setExpandedState(true);

            const firstBitsGroup = this.getGroupByGroupKey(this.similarSlideGroups?.results, slide.lshFirstBits);
            const middleBitsGroup = this.getGroupByGroupKey(this.similarSlideGroups?.results, slide.lshMiddleBits);
            const randomBitsGroup = this.getGroupByGroupKey(this.similarSlideGroups?.results, slide.lshRandomBits);

            let groupPhotos = [];
            let keys = [];

            if (firstBitsGroup?.photos) {
                groupPhotos = groupPhotos.concat(firstBitsGroup?.photos);
                keys.push(slide.lshFirstBits);
            }

            if (middleBitsGroup?.photos) {
                groupPhotos = groupPhotos.concat(middleBitsGroup?.photos);
                keys.push(slide.lshMiddleBits);
            }

            if (randomBitsGroup?.photos) {
                groupPhotos = groupPhotos.concat(randomBitsGroup?.photos);
                keys.push(slide.lshRandomBits);
            }

            if (groupPhotos.length > 0) {
                this.highlightGroup = {
                    groupType: this.groupTypes.SIMILAR_LSH_ANY,
                    items: groupPhotos,
                    keys: keys,
                };
                this.scrollToFirstInHighlightGroup();
            } else {
                this.resetHighlightGroup();
            }
        },
        totalGroupItems(pHash) {
            const group = this.getGroupByGroupKey(this.duplicateSlideGroups?.results, pHash);

            if (!group) return 0;

            return found?.photos.length || 0;
        },
        isDuplicate(pHash) {
            const group = this.getGroupByGroupKey(this.duplicateSlideGroups?.results, pHash);

            return !!group;
        },
        isSimilar(slide) {
            const { results } = this.similarSlideGroups ? this.similarSlideGroups : {};

            if (!results) return false;

            const keys = [slide.lshFirstBits, slide.lshMiddleBits, slide.lshRandomBits];

            for (const key of keys) {
                const group = this.getGroupByGroupKey(results, key);
                if (group) {
                    return true;
                }
            }
            return false;
        },
        isVideoSlide(arr, index) {
            if (!Array.isArray(arr)) return false;

            if (typeof index !== 'number') return false;

            return arr[index]?.mediaType === 1;
        },
        initCustomContext(e, item) {
            const customContext = this.$refs.customContext;
            if (!customContext) return;

            const selectedIds = this.getSelectedSlideIds();
            if (!selectedIds.includes(item.id)) {
                this.handleSlideClick(e, item, true);
            }

            customContext.handleCustomContext(e, item);
        },

        setContextItems() {
            const selectedCount = this.selectedSlides.length;

            const containsVideo = this.selectedSlides.some(s => s.mediaType === 1);

            let contextItems = [
                {
                    text: 'Download',
                    icon: 'fa-regular fa-arrow-down-to-bracket',
                    color: '#1877f2',
                    disabled: selectedCount <= 0,
                    value: 0,
                    callback: this.handleDownloadSelected,
                },
                {
                    text: 'Set Profile',
                    icon: 'fa-regular fa-user',
                    color: '',
                    disabled: selectedCount !== 1 || containsVideo,
                    value: 6,
                    callback: this.selectProfilePicture,
                },
                {
                    text: 'Edit Slide',
                    icon: 'fa-regular fa-pencil',
                    color: '',
                    disabled: selectedCount !== 1,
                    value: 3,
                    callback: this.editActiveSlide,
                },
                {
                    text: 'Rotate',
                    icon: 'fa-regular fa-rotate-right',
                    color: '',
                    disabled: selectedCount <= 0,
                    value: 4,
                    callback: this.rotateSelectedSlides,
                },
                {
                    text: 'Move to Start',
                    icon: 'fa-regular fa-arrow-left',
                    color: '',
                    disabled:
                        selectedCount <= 0 || this.tributeVideo.uploadingPhotos || this.selectedUploadUsers.length > 0,
                    value: 6,
                    callback: this.moveToFirst,
                },
                {
                    text: 'Move to Position',
                    icon: 'fa-regular fa-sort-circle',
                    color: '',
                    disabled:
                        selectedCount <= 0 || this.tributeVideo.uploadingPhotos || this.selectedUploadUsers.length > 0,
                    value: 9,
                    callback: () => {
                        this.moveToPositionIndex = this.selectedSlides?.[0]?.order + 1;
                        this.moveToPositionModal = true;
                    },
                },
                {
                    text: 'Move to End',
                    icon: 'fa-regular fa-arrow-right',
                    color: '',
                    disabled:
                        selectedCount <= 0 || this.tributeVideo.uploadingPhotos || this.selectedUploadUsers.length > 0,
                    value: 7,
                    callback: this.moveToLast,
                },
                {
                    text: 'Show in player',
                    icon: 'fa-regular fa-video',
                    color: '',
                    disabled:
                        selectedCount <= 0 || this.tributeVideo.uploadingPhotos || this.selectedUploadUsers.length > 0,
                    value: 7,
                    callback: this.jumpPlayerToSlideTime,
                },
                {
                    text: 'Delete',
                    icon: 'fa-regular fa-trash-can',
                    color: '#ff5252',
                    disabled: selectedCount <= 0 || this.tributeVideo.uploadingPhotos,
                    value: 5,
                    callback: this.openDeleteModal,
                },
            ];

            if (this.isContributorPage) {
                const excluded = [6, 7, 0, 9];
                contextItems = contextItems.filter(x => !excluded.includes(x.value));
            }
            if (this.state.isMobile) {
                const excludedMobile = [3];
                contextItems = contextItems.filter(x => !excludedMobile.includes(x.value));
            }
            this.contextItems = contextItems;

            this.contextRefreshKey++;
        },
        jumpPlayerToSlideTime() {
            if (this.selectedSlides.length === 0) {
                this.showSnackbar({ message: 'No slide selected', color: 'error' });
                return;
            }

            const slide = this.selectedSlides[0];

            if (!slide) return;

            const timestamp = this.msToSeconds(slide?.slideStartMs);

            if (!timestamp) {
                this.showSnackbar({
                    message: 'Slide timestamp not found, try again after re ordering slide',
                    color: 'error',
                });
                return;
            }

            this.$emit('set-preview-timestamp', timestamp + 0.75);
        },
        msToSeconds(val) {
            return val ? val / 1000 : 0;
        },
        async selectProfilePicture() {
            try {
                if (this.selectedSlides.length !== 1) {
                    throw new Error('Please select 1 photo to continue');
                }

                let slide = this.selectedSlides[0];
                const photoId = this.selectedSlides[0]?.id;

                if (!photoId) {
                    throw new Error('Invalid photo id');
                }

                slide.loading = true;
                await this.apiService.tributePhoto.updateProfilePhoto(this.eventId, photoId);
                slide.loading = false;

                this.showSnackbar({ message: 'Profile picture updated' });
            } catch (error) {
                console.log('error', error);
                this.showSnackbar({ message: error.message, color: 'error' });
            } finally {
            }
        },
        initSortable() {
            const container = this.$refs.sortableSlides;

            if (!container) return;

            this.sortableInstance = sortableHelper.initSortable(container, {
                onStart: this.onDragStart,
                onEnd: this.onDragEnd,
                onSelect: this.onSelect,
                onDeselect: this.onDeselect,
                scroll: container,
                scrollSensitivity: 150,
                delay: 120, // time in milliseconds to define when the sorting should start
                delayOnTouchOnly: true, // only delay if user is using touch
                touchStartThreshold: 20, // px, how many pixels the point should move before cancelling a delayed drag event
                filter: '.ignore-draggable',
            });
        },
        setSortableDisabled(bool) {
            if (!this.sortableInstance) return;

            this.sortableInstance.option('disabled', bool);
            this.sortableDisabled = bool;
        },
        handleWheel(event) {
            if (this.fetchingSlides || event.shiftKey || this.state.forceExpandedWorkspace) return;

            // How much a user needs to scroll before it triggers the expanding behavior
            const wheelBuffer = 50;
            // Access event.deltaY to get the scroll direction and amount
            const direction = event.deltaY > 0 ? 'down' : event.deltaY < 0 ? 'up' : '';
            const distance = Math.abs(event.deltaY);
            // console.log('Mousewheel:', { direction, distance, y: event.deltaY, event});
            // slides-container
            const slideContainer = document.querySelector('.slides-container');
            // Implement your custom logic here
            if (distance > wheelBuffer) {
                if (direction === 'down' && !this.isExpanded) {
                    this.tributeEditor.toggleExpandedState();
                }

                if (direction === 'up' && this.isExpanded && slideContainer.scrollTop < 5) {
                    // ensure slides-container is at the top before collapsing
                    // console.log(slideContainer);
                    this.tributeEditor.toggleExpandedState();
                }
            }
        },
        triggerSlideUpload() {
            if (this.$refs.actionBar?.$refs?.uploaderBtn) {
                this.$refs.actionBar.$refs.uploaderBtn.openFileSelection();
            }
        },
        onSelect(evt) {
            const selectedIds = this.selectedSlides.map(slide => slide.id);
            this.syncFromSelected(selectedIds);
        },
        onDeselect(evt) {
            const selectedIds = this.selectedSlides.map(slide => slide.id);
            this.syncFromSelected(selectedIds);
        },
        onDragStart(evt) {
            if (!evt.item) return;

            const id = this.parseSlideId(evt.item);
            if (!id) return;

            if (this.selectedSlides.some(x => x.id === id)) return;

            const found = this.reversedSlides.find(x => x.id === id);
            if (!found) return;

            this.selectedSlides.push(found);
        },

        async onDragEnd(evt) {
            try {
                const oldIndicies = map(evt.oldIndicies, 'index'),
                    newIndicies = map(evt.newIndicies, 'index');
                if (oldIndicies === newIndicies) return;
                const sortedNewIndicies = evt.newIndicies.slice().sort((a, b) => a.index - b.index);
                const slideIds = sortedNewIndicies
                    .map(item => {
                        let parsedInt = parseInt(item.multiDragElement.id.replace('slide-', ''));
                        return Number.isNaN(parsedInt) ? null : parsedInt;
                    })
                    .filter(Number.isInteger);

                let startingIndex = sortedNewIndicies[0].index;

                // sortable js is including intersection observer divs in index calc (including placeholders)
                // if (this.showLoadMore && startingIndex > 0) {
                //     startingIndex--;
                // }

                await this.reorderSlidesAbsolute(slideIds, startingIndex);
            } catch (error) {
                console.log(error, 'multi update error');
            }
        },
        previewSlide(event, index, slide) {
            this.deselectAllSlides();
            this.handleSlideClick(event, slide);
            this.previewingIndex = index;
            this.showSlidePreviewModal = true;
            this.$nextTick(() => {
                this.$refs.previewCarousel.previewingIndex = index;
            });
        },
        async actionFromPreviewImage(evt, action, newValue) {
            if (this.$props.readOnly) return;
            this.selectedSlides = [this.reversedSlides[this.previewingIndex]];

            // const loadingSlides = this.selectedSlides.filter(s => s?.loading);

            const selectedIds = this.selectedSlides.map(slide => slide.id);
            await this.syncFromSelected(selectedIds);

            if (!this.selectedSlides.length) {
                console.log('hmm no slides to perform action on');
                return;
            }

            if (action === 'edit') {
                this.editActiveSlide();
                this.showSlidePreviewModal = false;
            }
            if (action === 'rotate') {
                this.rotateSelectedSlides();
            }
            if (action === 'download') {
                this.handleDownloadSelected();
            }
            if (action === 'trash') {
                this.openDeleteModal();
            }
            const slide = this.selectedSlides[0];
            if (action === 'toggle-mute') {
                if (!slide?.id) {
                    throw new Error('Invalid slide id');
                }

                const val = !slide.mute;
                slide.mute = val;
                await this.setSlideMuted(slide, val);
            }
            if (action === 'update-duration') {
                // console.log({ evt, action, newValue });
                slide.slideDuration = newValue;
                await this.updateSlideDuration(slide, newValue);
            }
        },
        async getVideoPlayerBySlideId(slideId) {
            await this.$nextTick();
            const players = Array.isArray(this.$refs.videoPlayer) ? this.$refs.videoPlayer : [this.$refs.videoPlayer];

            return players.find(player => {
                // Handle both Vue components and DOM elements
                const element = player?.$el || player;
                return element && element.id === `video-preview-${slideId}`;
            });
        },
        getActivePreviewSlide() {
            if (!this.showSlidePreviewModal) return null;

            if (this.previewingIndex < 0) return null;

            const slide = this.reversedSlides[this.previewingIndex];

            if (!slide || !slide.id || slide.mediaType !== 1) return null;

            return slide;
        },
        async getActiveVideoPreview() {
            const slide = this.getActivePreviewSlide();

            if (!slide || !slide.id) return;
            const activePlayer = await this.getVideoPlayerBySlideId(slide.id);

            return activePlayer;
        },
        async setSlideMuted(slide, val) {
            try {
                if (!slide?.id) {
                    throw new Error('Invalid slide id');
                }

                const resp = await this.apiService.tributePhoto.setVideoSlideMute(slide.id, val);

                console.log(resp, ' mute toggle success');
            } catch (err) {
                console.log(err, 'error toggling slide mute');
            }
        },
        async updateSlideDuration(slide, val) {
            try {
                if (!slide?.id) {
                    throw new Error('Invalid slide id');
                }

                const resp = await this.apiService.tributePhoto.updateSlideDuration(slide.id, val);

                console.log(resp, ' slide duration updated');
            } catch (err) {
                console.log(err, 'error saving slide duration');
            }
        },
        async moveToFirst() {
            if (this.selectedSlides.length === 0) {
                this.showSnackbar({ message: 'Please select slides to move', color: 'error' });
                return;
            }

            const slideIds = this.selectedSlides.map(slide => slide.id);
            await this.reorderSlidesAbsolute(slideIds, 0);
        },
        async moveToPosition() {
            await this.$nextTick();

            if (this.selectedSlides.length === 0) {
                this.showSnackbar({ message: 'Please select slides to move', color: 'error' });
                return;
            }
            if (this.moveToPositionIndex - 1 < 0) {
                this.showSnackbar({ message: 'Invalid starting position', color: 'error' });
                return;
            }
            if (this.selectedSlides.length === 1) {
                if (this.moveToPositionIndex - 1 === this.selectedSlides[0].order) {
                    this.showSnackbar({
                        message: `Slide is already at position ${this.moveToPositionIndex}`,
                        color: 'error',
                    });
                    return;
                }
            }
            this.moveToPositionModal = false;
            const slideIds = this.selectedSlides.map(slide => slide.id);

            await this.reorderSlidesAbsolute(slideIds, this.moveToPositionIndex - 1);
            this.moveToPositionIndex = -1;
        },
        async moveToLast() {
            if (this.selectedSlides.length === 0) {
                this.showSnackbar({ message: 'Please select slides to move', color: 'error' });
                return;
            }

            const slideIds = this.selectedSlides.map(slide => slide.id);
            await this.reorderSlidesAbsolute(slideIds, this.totalSlides - slideIds.length); // Highest order = first position
        },
        async reorderSlidesAbsolute(slideIds, targetOrder) {
            try {
                if (this.pendingReorder) {
                    this.showSnackbar({
                        message: 'Please wait for current edit to finish',
                        color: 'error',
                    });
                    return;
                }

                this.pendingReorder = true;
                this.deselectAllSlides();
                this.updateLoadingSlides(slideIds);

                // Send to server
                await this.apiService.tributePhoto.multiUpdateOrder(this.tributeVideo.id, targetOrder, slideIds);

                // Remove moved slides from current view
                // this.reversedSlides = this.reversedSlides.filter(slide => !slideIds.includes(slide.id));

                this.showSnackbar({ message: 'Slides moved successfully' });
            } catch (error) {
                console.log(error, 'absolute reorder error');
                this.showSnackbar({ message: 'Error moving slides', color: 'error' });
            } finally {
                this.$emit('refresh-preview');
                setTimeout(() => {
                    this.updateLoadingSlides([]);
                    this.pendingReorder = false;
                }, 1000);
            }
        },
        async reorderSlidesLocal(slideIds, startingArrIndex) {
            try {
                const targetOrder = this.getTargetOrder(startingArrIndex, this.reversedSlides.length, this.totalSlides);

                if (this.pendingReorder) {
                    this.showSnackbar({
                        message: 'Please wait for current edit to finish',
                        color: 'error',
                    });
                    this.deselectAllSlides();
                    return;
                }

                this.pendingReorder = true;
                this.deselectAllSlides();
                this.updateLoadingSlides(slideIds);

                const slideMap = new Map(this.reversedSlides.map(slide => [slide.id, slide]));

                const orderedSlides = slideIds.map(id => slideMap.get(id)).filter(Boolean);

                const remainingSlides = this.reversedSlides.filter(slide => !slideIds.includes(slide.id));

                const updatedSlides = [
                    ...remainingSlides.slice(0, startingArrIndex),
                    ...orderedSlides,
                    ...remainingSlides.slice(startingArrIndex),
                ];

                const dedupedSlides = Array.from(new Map(updatedSlides.map(slide => [slide.id, slide])).values());
                this.reversedSlides = dedupedSlides;

                //May need to rethink error handling here,
                // but the response time feels much better for users
                var res = await this.apiService.tributePhoto.multiUpdateOrder(
                    this.tributeVideo.id,
                    targetOrder,
                    slideIds,
                );

                this.previewRefreshKey++;
            } catch (error) {
                console.log(error, 'multi update error');
                this.showSnackbar({ message: 'Error', color: 'error' });
            } finally {
                this.$emit('refresh-preview');
                setTimeout(() => {
                    this.updateLoadingSlides([]);
                    this.pendingReorder = false;
                }, 1000);
            }
        },
        updateLoadingSlides(slideIds) {
            this.reversedSlides = this.reversedSlides.map(slide => ({
                ...slide,
                loading: slideIds.includes(slide.id),
            }));
        },
        deselectAll() {
            sortableHelper.deselectAll(this.sortableInstance);
            this.selectedSlides = [];
        },

        syncFromSelected(ids) {
            return this.$nextTick(() => {
                sortableHelper.syncSelected(this.sortableInstance, ids, '#slide-');
            });
        },
        parseSlideId(element) {
            let parsedInt = parseInt(element.id.replace('slide-', ''));
            return Number.isNaN(parsedInt) ? null : parsedInt;
        },
        getSelectedSlideGUIDs() {
            const selectedSlides = sortableHelper.getSelectedElements(this.sortableInstance);
            return Array.from(selectedSlides).map(slide => slide.getAttribute('guid'));
        },
        getSelectedSlideIds() {
            const selectedSlides = sortableHelper.getSelectedElements(this.sortableInstance);

            return Array.from(selectedSlides).map(this.parseSlideId).filter(Number.isInteger);
        },
        //TODO: need to wire this up, holding for ui design
        async toggleSlideMute(slide) {
            if (slide.mediaType != 1) {
                this.showSnackbar({
                    message: 'Invalid slide media type selected',
                    color: 'error',
                });
                return;
            }

            try {
                slide.mute = !slide.mute;
                await this.apiService.tributePhoto.setVideoSlideMute(slide.id, slide.mute);
            } catch (error) {
                console.log(error, 'error toggling slide mute');
            }
        },
        async handleSlideClick(evt, slide) {
            if (this.$props.readOnly) {
                evt.preventDefault();
                return;
            }
            this.resetHighlightGroup();
            const action = multiSelectUtils.handleClick(evt, slide, {
                selectedItems: this.selectedSlides,
                allItems: this.reversedSlides,
                selectRangeStart: this.selectRangeStart,
            });

            if (Array.isArray(action.selectedItems) && action.type != 'none') {
                this.selectedSlides = action.selectedItems;
                this.selectRangeStart = action.selectRangeStart;
            }
        },
        async deselectAllSlides(event) {
            if (
                this.showDeleteModal ||
                this.showSlidePreviewModal ||
                this.showAddTextSlideModal ||
                this.showDownloadConfirmModal ||
                this.pendingRotate
            )
                return;

            this.deselectAll();
            this.selectedSlides = [];
            this.selectRangeStart = null;

            this.resetHighlightGroup();
        },
        async handlePreviewModalKeydown(evt) {
            // If modal is open in a modal let the keypresses be handled normally.
            // TODO: this may need revisting later to maybe be a mode toggle, like ON/OFF
            const activeModal = document.querySelector('.v-dialog__content--active');
            const previewModal = document.querySelector('.preview-modal');

            if (!activeModal || !previewModal) return;

            // Preview modal is open
            const previewCarousel = this.$refs.previewCarousel;
            switch (evt.code.toLowerCase()) {
                case 'arrowleft':
                    // go left
                    previewCarousel?.prev();
                    break;
                case 'arrowright':
                    // go right
                    previewCarousel.next();
                    break;
                case 'space':
                    this.actionFromPreviewImage(evt, 'edit');
                    break;
                case 'delete':
                    this.actionFromPreviewImage(evt, 'trash');
                    break;
                case 'keyr':
                    this.actionFromPreviewImage(evt, 'rotate');
                    break;
                case 'keyd':
                    this.actionFromPreviewImage(evt, 'download');
                    break;
                case 'keyf':
                    this.slidePreviewFullScreen = !this.slidePreviewFullScreen;
                    break;
                case 'escape':
                    this.showSlidePreviewModal = false;
                    break;
                default:
                    return;
            }
        },
        async handleSlideKeydown(evt) {
            // console.log(evt);
            if (evt.code === 'F5') {
                return;
            }
            if (!evt.metaKey) {
                evt.preventDefault();
            }

            const firstSlide = document.querySelector('.slide');

            const action = await multiSelectUtils.handleKeydown(evt, {
                selectedItems: this.selectedSlides,
                allItems: this.reversedSlides,
                selectRangeStart: this.selectRangeStart,
                containerWidth: this.$refs.sortableSlides.offsetWidth,
                itemWidth: firstSlide ? firstSlide.offsetWidth : 0,
                fetchAllItemsCallback: this.fetchAllSlides,
                deleteItemsCallback: this.openDeleteModal,
            });

            if (action && Array.isArray(action.selectedItems) && action.type != 'none') {
                this.selectedSlides = action.selectedItems;
                this.selectRangeStart = action.selectRangeStart;

                const indexToFocus = this.determineFocusIndex(action);
                if (indexToFocus != null) {
                    this.focusImage(indexToFocus);
                }
            }
        },
        determineFocusIndex(action) {
            if (this.selectedSlides.length == 1) {
                return this.reversedSlides.indexOf(this.selectedSlides[0]);
            }

            if (this.selectedSlides.length > 1) {
                const endSelectedIndex = this.reversedSlides.indexOf(
                    this.selectedSlides[this.selectedSlides.length - 1],
                );
                const startSelectedIndex = this.reversedSlides.indexOf(this.selectedSlides[0]);

                return endSelectedIndex === action.selectRangeStart ? startSelectedIndex : endSelectedIndex;
            }

            return null;
        },
        async fetchAllSlides() {
            try {
                if (this.fetchingAllSlides) return;

                this.fetchingAllSlides = true;

                if (this.reversedSlides.length === this.totalSlides) {
                    return this.reversedSlides;
                }

                let data = {
                    reversed: true,
                    pageSize: this.totalSlides,
                };

                const allSlides = await this.replaceCurrentPhotos(data);

                if (allSlides) {
                    return allSlides;
                }

                return null;
            } finally {
                this.fetchingAllSlides = false;
                this.fetchingSlides = false;
            }
        },
        getSlideDbIndex(index) {
            let hiddenSlides = 0;

            if (this.totalSlides > this.reversedSlides.length) {
                hiddenSlides = this.totalSlides - this.reversedSlides.length;
            }

            return index + 1 + hiddenSlides;
        },
        handleSlidesStartIntersect(isIntersecting) {
            if (!this.mounted || !isIntersecting) return;

            if (!this.fetchingSlides && this.reversedSlides.length < this.totalSlides) {
                this.debouncedNextPage();
            }
        },
        recordScrollPosition() {
            const container = this.$el.querySelector('.slides-container');
            if (container) {
                this.recordedScrollTop = container.scrollHeight - container.scrollTop;
                this.recordedScrollLeft = container.scrollWidth - container.scrollLeft;
            }
        },

        restoreScrollPosition() {
            requestAnimationFrame(() => {
                const container = this.$el.querySelector('.slides-container');
                if (container) {
                    container.scrollTop = container.scrollHeight - this.recordedScrollTop;
                    container.scrollLeft = container.scrollWidth - this.recordedScrollLeft;
                }
            });
        },
        recordScrollPercentage(isExpanded) {
            const container = this.$el.querySelector('.slides-container');
            if (!container) return;

            if (isExpanded) {
                const maxScroll = container.scrollHeight - container.clientHeight;
                this.currentScrollPercentage = maxScroll > 0 ? container.scrollTop / maxScroll : 0;
            } else {
                const maxScroll = container.scrollWidth - container.clientWidth;
                this.currentScrollPercentage = maxScroll > 0 ? container.scrollLeft / maxScroll : 0;
            }
        },

        restoreScrollPercentage(newIsExpanded) {
            const container = this.$el.querySelector('.slides-container');
            if (!container) return;

            // Clamp percentage to valid range
            const percentage = Math.max(0, Math.min(1, this.currentScrollPercentage));

            if (newIsExpanded) {
                // Switching TO expanded mode - apply percentage vertically
                const maxScroll = container.scrollHeight - container.clientHeight;
                const targetScroll = maxScroll * percentage;
                container.scrollTop = targetScroll;
            } else {
                // Switching TO collapsed mode - apply percentage horizontally
                const maxScroll = container.scrollWidth - container.clientWidth;
                const targetScroll = maxScroll * percentage;
                container.scrollLeft = targetScroll;
            }
        },
        async slidesNextPage() {
            if (this.pendingUploads.length > 0 || this.reversedSlides.length >= this.totalSlides) return;

            try {
                this.fetchingSlides = true;

                this.pageNumber++;
                console.log('next page', this.pageNumber);

                let data = {
                    reversed: true,
                    pageNumber: this.pageNumber,
                    pageSize: this.pageSize,
                };

                if (this.selectedUploadUsers.length) {
                    data.uploadUsers = this.selectedUploadUsers;
                }

                this.recordScrollPosition();
                var resp = await this.apiService.tributePhoto.getPhotos(this.eventId, data);

                if (resp.data.photos) {
                    let nextPageSlides = resp.data.photos.slice().reverse();

                    this.reversedSlides = [
                        ...nextPageSlides.map(slide => ({ ...slide, rotation: 0, loading: false })),
                        ...this.reversedSlides,
                    ];
                }

                if (resp.data.total) {
                    this.totalSlides = resp.data.total;
                    this.tributeVideo.updateTributeVideo({
                        totalSlidesCount: resp.data.total,
                    });
                }
                // Force a refresh on the preview modal
                this.previewRefreshKey++;
                // Check if any placeholders are still in view after load completes
                // await this.$nextTick();
                this.restoreScrollPosition();

                setTimeout(() => {
                    if (
                        !this.fetchingSlides &&
                        this.areAnyPlaceholdersVisible() &&
                        this.reversedSlides.length < this.totalSlides
                    ) {
                        // Use debounced version for recursive calls
                        this.debouncedNextPage();
                    }
                }, 250);
            } catch (error) {
                console.log(error, 'error fetching slides');
            } finally {
                this.fetchingSlides = false;
            }
        },
        areAnyPlaceholdersVisible() {
            const container = this.$el.querySelector('.slides-container');
            if (!container) return false;

            const placeholders = container.querySelectorAll('.placeholder');

            for (let placeholder of placeholders) {
                const rect = placeholder.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();

                // Check if placeholder is visible within container bounds
                const isVisible =
                    rect.top < containerRect.bottom &&
                    rect.bottom > containerRect.top &&
                    rect.left < containerRect.right &&
                    rect.right > containerRect.left;

                if (isVisible) {
                    return true;
                }
            }

            return false;
        },
        // This method should not alter current pageNumber or pageSize to
        // avoid interfering with infinite scroll pagination
        //Just reload and replace the current loaded images
        async replaceCurrentPhotos(params) {
            try {
                let resp;

                if (this.state.isContributor) {
                    const sessionId = getTributeUploadSessionId();
                    resp = await this.apiService.tributePhoto.getSessionPhotos(this.eventId, sessionId, params);
                } else {
                    resp = await this.apiService.tributePhoto.getPhotos(this.eventId, params);
                }

                if (resp.data) {
                    await this.$nextTick();
                    const tmpSlides = this.reversedSlides.slice();
                    this.reversedSlides = resp.data.photos
                        .slice()
                        .reverse()
                        .map(slide => {
                            const index = findIndex(tmpSlides, { id: slide.id });
                            return { ...slide, rotation: 0, loading: tmpSlides?.[index]?.loading || false };
                        });

                    this.$nextTick(() => {
                        // If scroll position is not at the bottom, do not jump to bottom
                        if (!this.mounted || this.isGalleryAtScrollEnd(this.isExpanded)) {
                            this.scrollGalleryToEnd();
                        }
                        this.mounted = true;
                    });
                }

                if (resp.data.total >= 0) {
                    this.totalSlides = resp.data.total;
                }

                return this.reversedSlides;
            } catch (error) {
                console.log(error, 'error updating current photos');
            }
        },
        async deleteSelectedSlides() {
            try {
                this.pendingDelete = true;
                this.block(true);
                let selectedIds = this.getSelectedSlideIds();

                if (selectedIds.length === 0) throw new Error('No valid slides selected');
                let resp;
                // For anon contributor users hand off the delete call
                if (this.state.isContributor) {
                    const sessionId = getTributeUploadSessionId();
                    resp = await this.apiService.tributePhoto.deletePhotoBatchSession(selectedIds, sessionId);
                } else {
                    resp = await this.apiService.tributePhoto.deletePhotoBatch(selectedIds);
                }
                const { data: deletedResults } = resp;
                console.log('does flatMap exist?', flatMap);
                // combine guids and ids with selected so that it can get filtered out correctly.
                selectedIds = [
                    ...selectedIds,
                    ...flatMap(deletedResults, deletedSlide => {
                        return [deletedSlide.guid, deletedSlide.id];
                    }),
                ];

                this.totalSlides -= selectedIds.length;
                this.reversedSlides = this.reversedSlides.filter(x => !selectedIds.includes(x?.guid || x.id));
                // update the tributeVideoStore
                this.tributeVideo.updateTributeVideoSelectedPhotos(structuredClone(this.reversedSlides));

                this.deselectAllSlides();
                this.$emit('refresh-preview');
            } catch (error) {
                console.log(error, 'error deleting slide');
            } finally {
                this.pendingDelete = false;
                this.block(false);
                this.tributeVideo.updateTributeVideo({
                    totalSlidesCount: this.reversedSlides.length,
                });
                if (this.tributeVideo.serviceId) {
                    this.refreshDuplicateAndSimilarSlides(this.tributeVideo.serviceId);
                }
            }
        },
        async revertPhoto(photo) {
            this.closeAddTextSlideModal();
            if (photo.originalPhoto == null) {
                this.showSnackbar({ message: 'No photo to revert to' });
                return;
            }

            this.apiService.tributePhoto.revertPhotoToOriginal(photo.id).then(resp => {
                this.reversedSlides[this.editingPhoto.slideIndex] = resp.data;
                this.editingPhoto = null;
                this.showSnackbar({ message: 'Original photo restored' });
            });
        },
        deletePhotoById() {
            this.closeAddTextSlideModal();
            this.$nextTick(async () => {
                const selectedId = this.editingPhoto.photo.id;
                const resp = await this.apiService.tributePhoto.deletePhoto(selectedId);
                this.reversedSlides = this.reversedSlides.filter(x => selectedId !== x.id);
                // update the tributeVideoStore
                this.tributeVideo.updateTributeVideoSelectedPhotos(structuredClone(this.reversedSlides));
                this.$emit('refresh-preview');
                this.editingPhoto = null;
                this.showSnackbar({ message: 'Media deleted successfully' });
                this.tributeVideo.updateTributeVideo({
                    totalSlidesCount: this.reversedSlides.length,
                });
            });
        },
        scrollGalleryToEnd() {
            const container = this.$el.querySelector('.slides-container');
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    left: container.scrollWidth,
                    behavior: 'instant',
                });
            }
        },

        isGalleryAtScrollEnd(vertical = true) {
            const container = this.$el.querySelector('.slides-container');
            if (!container) {
                return false;
            }

            // Check if scroll position is at or very close to the bottom
            const tolerance = 1;

            if (vertical) {
                const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - tolerance;
                return isAtBottom;
            } else {
                const isAtRight = container.scrollLeft + container.clientWidth >= container.scrollWidth - tolerance;
                return isAtRight;
            }
        },

        moveSlideForward() {
            if (this.selectedSlides.length !== 1) {
                this.showSnackbar({ message: 'Please select 1  slide to move', color: 'error' });
                return;
            }

            const slide = this.selectedSlides[0];
            const index = this.reversedSlides.findIndex(x => x.id === slide.id);

            if (index === -1) {
                this.showSnackbar({ message: 'Slide not found', color: 'error' });
                return;
            }

            if (index + 1 >= this.totalSlides) {
                return;
            }

            this.reorderSlidesLocal([slide.id], index + 1);
        },
        moveSlideBackward() {
            if (this.selectedSlides.length !== 1) {
                this.showSnackbar({ message: 'Please select 1  slide to move', color: 'error' });
                return;
            }

            const slide = this.selectedSlides[0];
            const index = this.reversedSlides.findIndex(x => x.id === slide.id);

            if (index === -1) {
                this.showSnackbar({ message: 'Slide not found', color: 'error' });
                return;
            }

            if (index <= 0) {
                return;
            }

            this.reorderSlidesLocal([slide.id], index - 1);
        },
        //Converts current "page" slide index to target db order
        getTargetOrder(arrIndex, arrLength, totalSlides) {
            // placeholder items affect sortable js inicies output
            const invertedIndex = arrLength + this.requiredPlaceholders - arrIndex;

            return totalSlides - invertedIndex;
        },
        editSlide(event, index, slide) {
            // ensure slide is set as selected...
            this.selectedSlides = [slide];
            // then edit active slide
            this.editActiveSlide();
        },
        async editAdjacentSlide(currentSlideId, dir) {
            if (this.saveInProgress) {
                await this.saveInProgress;
            }
            const selectedSlideIndex = this.reversedSlides.findIndex(x => x.id === currentSlideId);
            if (dir === 'next') {
                if (this.reversedSlides?.[selectedSlideIndex + 1]) {
                    this.editSlide({}, selectedSlideIndex + 1, this.reversedSlides?.[selectedSlideIndex + 1]);
                }
            } else {
                if (this.reversedSlides?.[selectedSlideIndex - 1]) {
                    this.editSlide({}, selectedSlideIndex - 1, this.reversedSlides?.[selectedSlideIndex - 1]);
                }
            }
        },
        async editActiveSlide() {
            if (this.selectedSlides.length > 1) {
                this.showSnackbar({ message: 'Please select 1 slide to edit', color: 'error' });
                return;
            }

            if (this.selectedSlides.length === 0) {
                this.showSnackbar({ message: 'No slide selected', color: 'error' });
                return;
            }

            const slide = this.selectedSlides[0];
            if (slide.mediaType === 1 && !ENABLE_VIDEO_EDITING) throw new Error('Unsupported media type');

            const selectedSlideIndex = this.reversedSlides.findIndex(x => x.id === slide.id);
            // console.log(selectedSlideIndex);
            const prevSlide = this.reversedSlides?.[selectedSlideIndex - 1] || null,
                nextSlide = this.reversedSlides?.[selectedSlideIndex + 1] || null;

            this.openAddTextSlideModal();

            this.$nextTick(async () => {
                if (slide?.sceneDataURL) {
                    const scene = await this.fetchSceneData(slide.sceneDataURL);
                    this.$refs.imglyEditor.loadScene(scene, slide.id, slide.mediaType, {
                        slide,
                        prevSlide,
                        nextSlide,
                    });
                } else {
                    let url = slide.url;
                    console.log(slide.url, 'slide url b4');

                    // Force mp4 format for imgly editor
                    if (slide.mediaType === 1) {
                        if (!slide.url.includes(process.env.VUE_APP_IMG_KIT_BASE)) {
                            const path = slide.url.split(`/tribute-photos/`)[1];
                            url = `${process.env.VUE_APP_IMG_KIT_BASE}${path}`;
                        }

                        // Parse existing URL to preserve any query parameters
                        const urlObj = new URL(url);
                        const existingTr = urlObj.searchParams.get('tr');

                        // Append f-mp4 to existing tr parameter or set it if none exists
                        if (existingTr) {
                            urlObj.searchParams.set('tr', `${existingTr},f-mp4`);
                        } else {
                            urlObj.searchParams.set('tr', 'f-mp4');
                        }

                        url = urlObj.toString();
                    }

                    this.$refs.imglyEditor.createSceneFromURL(url, slide.id, slide.mediaType, {
                        slide,
                        prevSlide,
                        nextSlide,
                    });
                }
            });
        },
        openDeleteModal() {
            if (this.isAnyImageSelected) {
                this.showDeleteModal = true;
            }
        },
        triggerPhotoUpload() {
            this.$refs.fileInput.click();
        },
        createProgressHandler(file) {
            return progressEvent => {
                const currentBytes = this.uploadedBytes + progressEvent.loaded;
                const percent = Math.ceil((currentBytes / this.totalBytes) * 100);

                this.progress = percent;
            };
        },
        // Upload media and return SAS_INFO
        async performMediaUpdate(eventId, file, progressCallback) {
            let { data: SAS_INFO } = await this.apiService.tributePhoto.getUploadUrl(eventId, file);

            // TODO: add cancel token
            await this.apiService.blob.upload(SAS_INFO.sas, file, {
                onUploadProgress: progressEvent => {
                    // console.log('onUploadProgress', { progressEvent});
                    const progress = Math.ceil((progressEvent.loaded / progressEvent.total) * 100);
                    this.handleTotalUploadProgress(progress);
                    if (progressCallback && typeof progressCallback === 'function') {
                        progressCallback(progress);
                    }
                },
            });
            return { SAS_INFO };
        },
        // Upload a new text slide
        async uploadTextSlide(eventId, file, metadata, progressCallback) {
            try {
                const sessionId = getTributeUploadSessionId();

                var { data: remainingAllowed } = await this.apiService.tributePhoto.getRemainingAllowedUploads(
                    eventId,
                    sessionId,
                );

                if (remainingAllowed.remaining <= 0) {
                    throw new Error(`Cannot exceed remaind allowed uploads: ${remainingAllowed.remaining}`);
                }
                const { SAS_INFO } = await this.performMediaUpdate(eventId, file, progressCallback);

                let tributePages = [
                    'TributeUploadPage',
                    'TributeFamilyPage',
                    'TributeVideoFamily',
                    'TributeVideoContributor',
                ];

                let data = [
                    {
                        duration: metadata?.duration ? Math.round(metadata.duration) : 0,
                        mediaType: metadata?.mediaType || 2,
                        name: file.name,
                        url: SAS_INFO.sas.split('?sv=')[0],
                        uniqueName: SAS_INFO.fileName,
                        uploadSource: tributePages.includes(this.$route.name) ? 2 : 0,
                        uploadUserName: tributePages.includes(this.$route.name)
                            ? this.state?.anonUserDetails?.name || 'Unknown'
                            : this.$auth.user?.name,
                        uploadUserRelationship: tributePages.includes(this.$route.name)
                            ? this.state?.anonUserDetails?.relationship || 'Unknown'
                            : 'Funeral Staff',
                    },
                ];

                var resp = await this.apiService.tributePhoto.createPhotoBatch(eventId, data, false);
                this.tributeVideo.updateTributeVideo({
                    // Set lastSyncedChange, in case we need to delay render
                    lastSyncedChange: moment().toISOString(),
                });
                this.scrollGalleryToEnd();
                this.$emit('refresh-preview');

                if (resp.data.length > 0) {
                    return resp.data[0];
                }

                return null;
            } catch (error) {
                console.log('Error uploading text slide', error);
            }
        },
        async fetchSceneData(sceneDataURL) {
            try {
                const resp = await withErrorReporting(() => {
                    return this.axios.get(sceneDataURL);
                });

                if (resp.data.scene) {
                    return resp.data.scene;
                }

                return null;
            } catch (error) {
                console.log('Error fetching scene data', error);
            }
        },
        generateJsonBlob(jsonData) {
            const jsonString = JSON.stringify(jsonData);

            const jsonBlob = new Blob([jsonString], { type: 'application/json' });

            return jsonBlob;
        },
        async updateSlideScene(slide, scene, sessionId) {
            try {
                const resp = await this.apiService.tributePhoto.getSlideSceneUploadUrl(slide.id);
                const { sas, fileName } = resp.data;

                const jsonData = {
                    slideId: slide.id,
                    lastModified: new Date().toISOString(),
                    scene: scene,
                };

                var sceneBlob = this.generateJsonBlob(jsonData);

                await this.apiService.blob.upload(sas, sceneBlob);
                const updatedSlideResp = await this.apiService.tributePhoto.updateSlideScene(
                    slide.id,
                    fileName,
                    sessionId,
                );

                this.tributeVideo.updateTributeVideo({
                    // Set lastSyncedChange, in case we need to delay render
                    lastSyncedChange: moment().toISOString(),
                });
                return updatedSlideResp.data;
            } catch (error) {
                console.log('error uploading scene json', error);
            }
        },
        async onEditorSave(editorResults) {
            this.resetEditorProgress();
            const { metadata, slideId, scene, blob, keepEditorOpen } = editorResults;
            if (!keepEditorOpen) {
                this.closeAddTextSlideModal();
            }
            let { promise, resolve } = Promise.withResolvers();
            this.saveInProgress = promise;
            // Are we editing an existing slide or is this a new one?
            const isEditing = slideId && slideId > -1;
            let index = this.reversedSlides.findIndex(slide => slide?.id === slideId);
            const slide = this.reversedSlides?.[index] || null;

            const fileName = isEditing
                ? slide?.name
                : metadata.mediaType !== 1
                ? 'Title Slide.jpeg'
                : 'Title Slide.mp4';

            const sessionId = getTributeUploadSessionId();

            console.log('FILENAME', fileName);
            // console.log('selectedSlides', this.selectedSlides, { editorResults, slide, slideId });
            blob.name = fileName;
            this.showUploadProgress = true;
            if (isEditing) {
                // save slide changes...
                if (slide) {
                    // Set loading state on slide
                    slide.loading = true;
                }
                // slide = this.reversedSlides[index] = editorResults;
                const { SAS_INFO } = await this.performMediaUpdate(this.tributeVideo.eventId, blob);
                await this.apiService.tributePhoto.replacePhoto(
                    slide.id,
                    SAS_INFO.fileName,
                    true,
                    sessionId,
                    metadata?.duration,
                );
                await this.$nextTick();
                if (slide) {
                    const updatedSlideData = await this.updateSlideScene(slide, scene, sessionId);
                    // console.log(`Slide updated`, updatedSlideData);
                    const updatedIndex = this.reversedSlides.findIndex(tmpSlide => tmpSlide.id === slide.id);
                    // console.log('comparing Indexes', { index, updatedIndex });
                    Object.assign(this.reversedSlides[updatedIndex], {
                        ...updatedSlideData,
                        loading: slide.loading,
                    });
                    // console.log(`Saving edit on slide ${slide.id} on index ${index}`);
                    setTimeout(async () => {
                        await this.$nextTick();
                        // TODO: See about getting this to clear after the new image loads and not on a timeout
                        if (this.reversedSlides[updatedIndex].loading) {
                            this.reversedSlides[updatedIndex].loading = false;
                        }
                    }, 2500);
                }
            } else {
                this.tributeVideo.updateTributeVideo({
                    // Set lastSyncedChange, in case we need to delay render
                    uploadingPhotos: true,
                });
                const tmpSlide = {
                    id: `tmpSlide-${this.totalSlides + 1}`,
                    type: 'image/jpeg',
                    data: blob,
                    progress: {
                        percentage: 0,
                    },
                };
                this.pendingUploads.push(tmpSlide);
                const tmpPendingIndex = this.pendingUploads.findIndex(tmpPending => tmpPending.id === tmpSlide.id);
                // New Slide being added
                const newSlide = await this.uploadTextSlide(this.tributeVideo.eventId, blob, metadata, progress => {
                    tmpSlide.progress.percentage = progress;
                });
                this.scrollGalleryToEnd();
                if (scene && newSlide) {
                    const updatedSlideData = await this.updateSlideScene(newSlide, scene);
                    this.pendingUploads.splice(tmpPendingIndex, 1);
                    await this.$nextTick();
                    setTimeout(() => {
                        // Slides can be added automatically by websockets so delay the manual adding in case it already exists...
                        const alreadyExisting = document.querySelector(`#slide-${updatedSlideData.id}`);
                        if (!alreadyExisting) {
                            console.log("doing manual add of new slide because it's not in list... ");
                            this.reversedSlides.push(updatedSlideData);
                            this.focusSlideById(updatedSlideData.id);
                            this.tributeVideo.updateTributeVideo({
                                totalSlidesCount: this.reversedSlides.length,
                            });
                        }
                    }, 1200);
                }
            }
            this.showUploadProgress = false;
            this.tributeVideo.updateTributeVideo({
                uploadingPhotos: false,
                totalSlidesCount: this.reversedSlides.length,
            });
            this.editingPhoto = null;
            resolve();
            this.saveInProgress = null;
        },
        addNewTextSlide() {
            // When adding a new text slide, first clear out any existing selected image
            if (this.selectedSlides.length > 0) {
                this.deselectAllSlides();
            }
            // Then open up the editor modal
            this.openAddTextSlideModal();
        },
        async createTextSlide() {
            await this.openAddTextSlideModal();
            const editor = this.$refs.imglyEditor;
            editor.createTextScene();
        },
        async openAddTextSlideModal() {
            this.showAddTextSlideModal = true;
            await this.$nextTick();
        },
        closeAddTextSlideModal() {
            this.showAddTextSlideModal = false;
        },
        handleTempFilesChange(tempFiles) {
            if (tempFiles.length === 0) return;

            this.pendingUploads = tempFiles;
        },
        handleTotalUploadProgress(val) {
            if (val > this.uploadProgressPercent) {
                this.uploadProgressPercent = val;
            }
        },
        handleSuccessfulUploads(newSlides) {
            this.$emit('refresh-preview');
            // //Handle batched upload responses
            const newSlideIds = new Set(newSlides.map(slide => slide.uppyId));

            for (let i = this.pendingUploads.length - 1; i >= 0; i--) {
                if (newSlideIds.has(this.pendingUploads[i].id)) {
                    this.pendingUploads.splice(i, 1);
                }
            }

            const existingIds = new Set(this.reversedSlides.map(slide => slide.id));

            const uniqueNewSlides = newSlides
                .filter(slide => !existingIds.has(slide.dbData.id))
                .map(slide => {
                    const index = findIndex(this.reversedSlides, { id: slide.id });
                    return {
                        ...slide.dbData,
                        rotation: 0,
                        loading: this.reversedSlides?.[index]?.loading || false,
                        // _tmpData: finishedUploadMap[slide.uppyId].data
                    };
                });

            this.reversedSlides.push(...uniqueNewSlides);

            this.tributeVideo.updateTributeVideoSelectedPhotos(structuredClone(this.reversedSlides));

            this.tributeVideo.updateTributeVideo({
                // Set lastSyncedChange, in case we need to delay render
                lastSyncedChange: moment().toISOString(),
                totalSlidesCount: this.reversedSlides.length,
            });

            this.totalSlides = this.reversedSlides.length;

            this.getUploadUsers(this.tributeVideo.id);
        },
        onFileAdded: debounce(function () {
            this.showUploadProgress = true;
            this.tributeVideo.updateTributeVideo({
                // Set lastSyncedChange, in case we need to delay render
                uploadingPhotos: true,
            });
            this.scrollGalleryToEnd();
            // Listen for files being added then trigger the upload automatically
            this.$refs.actionBar.initUpload(this.$props.eventId).then(newSlides => {
                // all uploads should be finished here, so set percentage to 100
                each(this.pendingUploads, up => {
                    if (up.progress.percentage < 100) {
                        up.progress.percentage = 100;
                    }
                });

                this.pendingUploads = [];
                this.deselectAllSlides();

                this.tributeVideo.updateTributeVideo({
                    // Set lastSyncedChange, in case we need to delay render
                    uploadingPhotos: false,
                });

                setTimeout(() => {
                    this.resetUploadState();
                }, 1000);
                //Refresh from webhook after ingest pipeline finishes processing
            });
        }, 300),
        async rotateSelectedSlides() {
            try {
                const selectedSlides = [...this.selectedSlides];

                this.currentRotation = (this.currentRotation + 90) % 360;

                const rotation = this.currentRotation;

                selectedSlides.forEach(slide => {
                    this.$set(slide, 'rotation', rotation);
                    this.$set(slide, 'lodaing', true);
                });

                if (rotation > 0) {
                    this.pendingRotate = true;
                    this.tributeVideo.updateTributeVideo({
                        rotatingPhotos: true,
                        ingestPipelineCount: selectedSlides.length,
                    });

                    this.debouncedMultiSlideRotate(selectedSlides, rotation);
                } else {
                    this.debouncedMultiSlideRotate?.cancel?.();
                    this.pendingRotate = false;
                    this.tributeVideo.updateTributeVideo({
                        rotatingPhotos: false,
                    });
                }
            } catch (error) {
                console.log(error, 'error rotating selected images');
            }
        },
        async initMultiSlideRotate(slides, rotation) {
            try {
                console.log('multi roatate requested');
                this.deselectAllSlides();

                slides.forEach(slide => {
                    this.$set(slide, 'loading', true);
                });

                const ids = slides.map(slide => slide.id);
                const resp = await this.apiService.tributePhoto.triggerRotatePhotoFunction(ids, rotation);

                // // slides.forEach(slide => {
                // //     slide.loading = false;
                // // });

                const selectedIds = this.selectedSlides.map(slide => slide.id);
                this.syncFromSelected(selectedIds);

                this.debouncedSocketRefresh();
            } catch (error) {
                console.log('error in debounced rotate', error);
            } finally {
                this.currentRotation = 0;
                this.pendingRotate = false;
                this.tributeVideo.updateTributeVideo({
                    rotatingPhotos: false,
                });
            }
        },

        async handleSocketRefresh() {
            // Contributors do not see others' photos,
            // skip the photo refresh on socket trigger for contributors
            if (this.state.isContributor || this.pendingUploads.length) return;

            this.pageNumber = 0;
            let pageSizeIcrement = this.pendingReorder ? 0 : 1;
            this.pageSize = Math.max(24, this.reversedSlides.length + pageSizeIcrement);

            let data = {
                reversed: true,
                pageNumber: this.pageNumber,
                pageSize: this.pageSize,
            };

            if (this.selectedUploadUsers.length) {
                data.uploadUsers = this.selectedUploadUsers;
            }

            await this.replaceCurrentPhotos(data);

            if (this.tributeVideo.serviceId) {
                this.refreshDuplicateAndSimilarSlides(this.tributeVideo.serviceId);
            }

            this.getUploadUsers(this.tributeVideo.id);

            const selectedIds = this.selectedSlides.map(slide => slide.id);
            this.syncFromSelected(selectedIds);

            // Force a refresh on the preview modal
            this.previewRefreshKey++;

            if (this.showSlidePreviewModal && selectedIds.length === 1) {
                const index = this.reversedSlides.findIndex(x => x.id === selectedIds[0]);
                if (index !== -1) {
                    this.previewingIndex = index;
                }
            }
        },

        focusImage(index) {
            this.$nextTick(() => {
                const slide = this.$el.querySelectorAll('.slide')[index];
                if (slide) {
                    slide.scrollIntoView({ behavior: 'smooth', inline: 'center' });
                }
            });
        },
        focusSlideById(slideId) {
            console.log('called focus image on slide ID: ', slideId);
            this.$nextTick(() => {
                const slide = this.$el.querySelector(`#slide-${slideId}`);
                if (slide) {
                    slide.scrollIntoView({ behavior: 'smooth', inline: 'center' });
                }
            });
        },

        closeDeleteModal() {
            this.showDeleteModal = false;
        },
        confirmDelete() {
            this.closeDeleteModal();
            this.showSlidePreviewModal = false;
            this.previewingIndex = -1;
            this.deleteSelectedSlides();
        },
        handleDownloadSelected() {
            const photos = this.selectedSlides;

            if (photos.length === 0) return;

            if (photos.length == 1) {
                this.downloadImages(photos);
            } else {
                this.showDownloadConfirmModal = true;
            }
        },
        async handleDownloadFiltered() {
            if (this.totalSlides <= 0) {
                this.showSnackbar({
                    message: 'No slides available for download',
                    color: 'error',
                });
                return;
            }

            try {
                await this.filterSlidesBySelectedUsers(this.totalSlides);

                const photos = this.reversedSlides;

                this.downloadImages(photos);
            } catch (error) {
                console.log('error downloading filtered slides');
            }
        },
        async handleDownloadAll() {
            try {
                await this.fetchAllSlides();

                const photos = this.reversedSlides;

                this.downloadImages(photos);
            } catch (error) {
                console.log(error, 'error downloading all slides');
            }
        },
        confirmDownloadSelected() {
            this.showDownloadConfirmModal = false;
            this.downloadImages(this.selectedSlides);
        },
        getUniqueName(name, nameCountMap) {
            if (!nameCountMap.has(name)) {
                nameCountMap.set(name, 0);
                return name;
            }

            const count = nameCountMap.get(name) + 1;
            nameCountMap.set(name, count);

            const extIndex = name.lastIndexOf('.');
            const hasExt = extIndex !== -1;
            const base = hasExt ? name.slice(0, extIndex) : name;
            const ext = hasExt ? name.slice(extIndex) : '';

            return `${base} (${count})${ext}`;
        },
        async downloadImages(photos) {
            try {
                if (this.showDownloadProgress) return;

                this.showDownloadProgress = true;

                const promises = photos.map(async image => {
                    if (image.url) {
                        const blob = await this.getImageData(image);
                        return { ...image, blob: blob };
                    }
                });

                const res = await Promise.all(promises);

                //Download multiple as zip folder
                if (photos.length > 1) {
                    const zip = new JSZip();
                    const nameCount = new Map();

                    res.forEach(image => {
                        const uniqueName = this.getUniqueName(image.name, nameCount);
                        zip.file(uniqueName, image.blob);
                    });

                    const zipFile = await zip.generateAsync({ type: 'blob' });

                    this.downloadBlob(
                        zipFile,
                        `${this.tributeVideo.firstName} ${this.tributeVideo.lastName} Tribute Photos`,
                    );
                    this.showSnackbar({ message: 'Photos downloaded' });
                } else {
                    this.downloadBlob(res[0].blob, res[0].name);
                    this.showSnackbar({ message: 'Photo downloaded' });
                }
                this.deselectAllSlides();
            } catch (err) {
                console.log(err, 'error downloading images');
                this.showSnackbar({ message: 'Error downloading images', color: 'error' });
            } finally {
                setTimeout(() => {
                    this.resetDownloadState();
                }, 1000);
            }
        },
        async getImageData(photo) {
            if (!photo.url || !photo.id) return null;

            try {
                const response = await withErrorReporting(() => {
                    return this.axios({
                        method: 'get',
                        url: photo.url,
                        responseType: 'blob',
                        onDownloadProgress: evt => this.updateProgress(photo.id, evt.loaded, evt.total),
                    });
                });

                return response.data;
            } catch (error) {
                console.log(error, 'error');
                this.showSnackbar({ message: 'Error downloading image', color: 'error' });
            }
        },
        updateProgress(photoId, loaded, total) {
            this.downloadProgressMap[photoId] = Math.round((loaded / total) * 100);

            this.calculateOverallProgress();
        },
        calculateOverallProgress() {
            const totalProgress = Object.values(this.downloadProgressMap).reduce((acc, progress) => acc + progress, 0);
            const numItems = Object.keys(this.downloadProgressMap).length;
            const percent = Math.round(totalProgress / numItems);

            if (percent > this.downloadProgressPercent) {
                this.downloadProgressPercent = percent;
            }
        },
        downloadBlob(file, name) {
            const blob = window.URL.createObjectURL(file);
            const anchor = document.createElement('a');

            anchor.style.display = 'none';
            anchor.href = blob;
            anchor.download = name;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(blob);
        },
        resetDownloadState() {
            this.showDownloadProgress = false;
            this.downloadProgressMap = {};
            this.downloadProgressPercent = 0;
        },
        resetUploadState() {
            this.showUploadProgress = false;
            this.uploadProgressPercent = 0;
        },
        async getSimilarSlides(serviceId) {
            try {
                if (!serviceId) throw new Error('Invalid serviceId');
                const response = await this.apiService.tributePhoto.getCollectionSimilars(serviceId);

                if (response.data) {
                    return response.data;
                }
            } catch (error) {
                console.log(error, 'error fetching similar photos');
            }
        },
        async getDuplicateSlides(serviceId) {
            try {
                if (!serviceId) throw new Error('Invalid serviceId');
                const response = await this.apiService.tributePhoto.getCollectionDuplicates(serviceId);

                if (response.data) {
                    return response.data;
                }
            } catch (error) {
                console.log(error, 'error fetching duplicate photos');
            }
        },
        processDupesAndSimilars({ dupes, similars }) {
            this.duplicateSlideGroups = {
                results: dupes?.results || [],
                total: dupes?.total || 0,
            };

            this.similarSlideGroups = {
                results: similars?.results || [],
                total: similars?.total || 0,
            };
            this.totalDuplicateSlides = this.duplicateSlideGroups.total;
            this.totalSimilarSlides = this.similarSlideGroups.total;

            // if (dupes?.results) {
            //     this.totalDuplicateSlides = dupes.results.reduce((acc, current) => acc + current.count, 0);
            //     this.totalSimilarSlides = similars.results.reduce((acc, current) => acc + current.count, 0);
            // }

            const allGroups = [...dupes?.results, ...similars?.results];

            const validGroupKeys = allGroups.map(g => {
                return g.groupKey;
            });

            const validPhotoIds = new Set();
            allGroups.forEach(group => {
                group.photos.forEach(photo => {
                    validPhotoIds.add(photo.id);
                });
            });
            this.selectedGroups = this.selectedGroups
                .filter(group => validGroupKeys.includes(group.groupKey))
                .map(group => {
                    return {
                        ...group,
                        photos: group.photos.filter(photo => validPhotoIds.has(photo.id)),
                    };
                });
        },
        async refreshDuplicateAndSimilarSlides(serviceId) {
            // console.log('refreshDuplicateAndSimilarSlides', this.tributeVideo.slideCountWhenLastDupeCheck, this.tributeVideo.totalSlidesCount, this.dueForADupeCheck);

            if (this.state.isContributor) return;

            // If the slide count is the same then we can skip checking until it changes
            if (!this.dueForADupeCheck) {
                console.warn(
                    `Skipping fetching of DuplicateAndSimilarSlides because:`,
                    `${!this.dueForADupeCheck ? ' not due for a dupe check...' : ''}`,
                    `${
                        this.tributeVideo.slideCountWhenLastDupeCheck === this.tributeVideo.totalSlidesCount
                            ? 'slide count has not changed since last check...'
                            : ''
                    }`,
                );
                return;
            }
            this.tributeVideo.updateTributeVideo({
                slideCountWhenLastDupeCheck: this.tributeVideo.totalSlidesCount || 0,
                lastDupeCheckTimestamp: new Date().toISOString(),
            });
            const dupes = await this.getDuplicateSlides(serviceId);
            const similars = await this.getSimilarSlides(serviceId);

            this.processDupesAndSimilars({ dupes, similars });

            this.tributeVideo.updateTributeVideo({
                duplicateSlideCount: this.totalDuplicateSlides,
                similarSlideCount: this.totalSimilarSlides,
                duplicateSlideGroups: this.duplicateSlideGroups,
                similarSlideGroups: this.similarSlideGroups,
            });
        },
        shrinkSlide() {
            if (this.slideWidth >= this.slideWidthMin + this.slideWidthStepSize) {
                this.slideWidth -= this.slideWidthStepSize;
            }
        },
        growSlide() {
            if (this.slideWidth <= this.slideWidthMax + this.slideWidthStepSize) {
                this.slideWidth += this.slideWidthStepSize;
            }
        },
        async findSlideAndRevert(slideId) {
            const slide = this.reversedSlides.find(s => s.id === slideId);

            if (slide) {
                this.editingPhoto = slide;
                await this.revertPhoto(slide);
            }
        },
        removeSlideLoadingState(slide) {
            // console.log('Removing loading state from slide', slide.id);
            const index = findIndex(this.reversedSlides, { id: slide.id });
            this.reversedSlides[index].loading = false;
        },
    },
    sockets: {
        async NotifyUpload(data) {
            if (data?.event?.id != this.eventId) return;
            await this.$nextTick();
            if (
                this.tributeVideo.totalSlidesCount >= 100 &&
                this.tributeVideo.performanceMode === tributePerformanceModeEnum.FEATURES
            ) {
                this.tributeVideo.performanceMode = tributePerformanceModeEnum.BALANCED;
                setTributePerformanceMode(this.tributeVideo.performanceMode);
                this.showSnackbar({
                    message: `Performance mode set to Balanced now that slides count is at or over 100.`,
                });
            }
            if (this.pendingUploads.length > 0) return;
            if (this.pendingReorder || this.pendingDelete || this.pendingRotate) return;

            this.debouncedSocketRefresh();
        },
        async NotifyTributeJsonRefreshed(data) {
            if (this.tributeVideo.uploadingPhotos) return;
            const loadingSlides = this.reversedSlides.some(s => s?.loading);
            if (loadingSlides) return;
            if (this.tributeVideo?.id && data.id == this.tributeVideo.id) {
                this.debouncedSocketRefresh();
            }
        },
        NotifyIngestPipelineUpdate(data) {
            if (data.coreImageProcessingRunning) return;
            if (data.eventId === this.tributeVideo.eventId) {
                const found = this.reversedSlides.find(s => s.id === data.id);

                if (!found || !found?.loading) return;

                this.debouncedSocketRefresh();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.devMode-overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    background-color: rgba(0, 0, 0, 0.7);
    z-index: 5;
    color: white;
    padding: 8px;
    font-family: monospace;

    // padding-top: 45px;

    .devMode-contents {
        margin-top: 45px;
        // border: 2px solid red;
        max-width: 100%;
        max-height: calc(100% - 45px);
        overflow: auto;

        .dev-mode-detail {
            border-bottom: 1px solid rgba(255, 255, 255, 0.5);
        }
    }
}

.dev-mode-modal-content {
    white-space: pre-wrap;
    word-break: break-word;
    // color: white;
    padding: 2px;
}
.manage-photos-container:focus {
    outline: none;
}

.sortable-selected {
    .slide-image {
        border: 4px solid #ea580c;
    }
}

.count-overlay {
    display: none;
    position: absolute;
    inset: 0;
    background: #f35525;
    font-weight: bold;
    aspect-ratio: 1;

    &.chars-1 {
        font-size: 4.6rem;
    }
    &.chars-2 {
        font-size: 3.8rem;
    }
    &.chars-3 {
        font-size: 3.1rem;
    }
    &.chars-4 {
        font-size: 2.1rem;
        letter-spacing: -1.9px;
    }
}
.count-overlay-text {
    display: none;
    position: absolute;
    background: white;
    border: 1px solid $medium-gray;
    bottom: 20px;
    margin: auto;
    width: fit-content;
    left: 0;
    right: 0;
    top: auto;
}
.sortable-drag {
    .count-overlay {
        display: inline-flex;
        z-index: 200;
        margin: auto;
        align-items: center;
        justify-content: center;
        height: 100px;
        text-align: center;
        border-radius: 100%;
        aspect-ratio: 1;
        flex: 1;
    }
    .count-overlay-text {
        display: inline-flex;
        z-index: 200;
        margin: auto;
        align-items: center;
        justify-items: center;
        height: 36px;
        text-align: center;
        border-radius: 10px;
        padding: 0 10px;
    }

    .slide-image {
        border-radius: 6px;
        transform: rotate(-3deg) !important;
        border: 4px solid #ea580c;
    }
    .slide-order,
    .slide-user,
    .slide-bottom-right,
    .selected-indicator {
        display: none !important;
    }
}

.sortable-ghost {
    .slide-image {
        border: 2px solid #ea580c;
    }
}

.in-preview-highlight {
    background: #1a1a1a;
    border-radius: 10px;
    // box-shadow: 0 0 10px rgba(59, 130, 246, 0.7), 0 0 20px rgba(59, 130, 246, 0.5), 0 0 30px rgba(59, 130, 246, 0.1);
    box-shadow: 0 0 10px rgba(255, 83, 13, 0.7), 0 0 20px rgba(255, 83, 13, 0.5), 0 0 30px rgba(255, 83, 13, 0.1);
    transition: 0.2s;
}

.manage-photos-container {
    display: flex;
    flex-direction: column;
    @include mobile() {
        flex-direction: column-reverse;
    }
}

.slides-container {
    display: inline-flex;
    flex-wrap: nowrap;
    gap: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    transition: height 0.8s;
    height: 100%;
    padding: 10px 0;
}

.slides-container.expanded {
    flex-wrap: wrap;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 100%;
    // align-content: flex-start;
    // flex: 1;
    display: grid;
    grid-template-rows: repeat(var(--slides-per-row), 1fr);
    grid-template-columns: repeat(var(--slides-per-row), 1fr);

    // &::before {
    //     content: '';
    //     width: 0;
    //     padding-bottom: 100%;
    //     grid-row: 1 / 1;
    //     grid-column: 1 / 1;
    // }

    // & > *:first-child {
    //     grid-row: 1 / 1;
    //     grid-column: 1 / 1;
    // }
    @include mobile {
        margin-bottom: 20px;
        // grid-template-columns: repeat(auto-fit, minmax(33%, 1fr)) !important;

        .slide,
        .pending-slide {
            // width: min(242px, 48%);
            // pointer-events: none;
            height: auto;
            aspect-ratio: 1;
            max-height: 100%;
            flex-basis: auto;
            // max-width: 23vw;
            padding-top: 100%;
            max-width: initial;
        }
    }
}
.slides-container:not(.expanded) {
    .slide,
    .pending-slide {
        padding-top: 0;
        flex-shrink: 0;
    }
}
.slide,
.pending-slide {
    // height: 100%;
    // max-height: 500px;
    // min-height: 100px;
    aspect-ratio: 1 !important;

    border-radius: 8px;
    position: relative;
    // transition: transform 0.2s, border 0.2s;
    // flex-shrink: 0;
    display: inline-flex;
    overflow: hidden;
    flex-basis: auto;
    padding-top: 100%;
    max-width: var(--max-slide-width);
    max-height: var(--max-slide-width);
    @include mobile() {
        min-height: 100px;
    }
    .slide-image {
        aspect-ratio: 1;
        // width: 100%;
        // height: 100%;
        object-fit: cover;
        // border-radius: 8px;
        z-index: 0;
        background-color: #f1f4f7;
        position: absolute;
        inset: 0;
        width: 100%;
    }
    &.placeholder {
        ::v-deep {
            .v-skeleton-loader {
                position: absolute;
                inset: 0;
                .v-skeleton-loader__image {
                    height: 100%;
                }
            }
        }
    }
}
.slide.original-aspect {
    .slide-image {
        object-fit: contain;
    }
}
.pending-slide {
    img {
        opacity: 0.4;
    }

    .progress-indicator {
        width: 93%;
        display: inline-block;
        height: 5px;
        bottom: 10px;
        position: absolute;
        left: 0;
        right: 0;
        background: #ffedd5;
        margin: auto;
        border-radius: 10rem;
    }

    .progress-slider {
        height: 100%;
        background: #f97316;
        border-radius: 10rem;
        transition: width 200ms ease-in-out;
    }
}

// .slide.dragging {
//     transform: rotate(-3deg) !important;
//     border: 4px solid #ea580c;
// }

// .slide.selected .slide-image {
//     border: 4px solid #ea580c;
// }

.slide-bottom-right {
    position: absolute;
    z-index: 2;
    bottom: 8px;
    right: 8px;
    display: flex;
    gap: 4px;
}

.slide-preview-inner,
.slide-order {
    min-width: 42px;
    height: 32px;
    background: white;
    border: 1px solid #d1d5db;
    box-shadow: 0px 1px 2px 0px #0000000d;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 2.5%;
    padding: 0 12px;
}

.slide-order {
    transform: translateY(0);
    transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-preview,
.slide-user {
    transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1);
    // transition-delay: 200ms;
    opacity: 0;
    transform: translateY(80px);
    @include mobile() {
        opacity: 1 !important;
        transform: translateY(0px) !important;
    }
}
.slide-preview {
    transition-duration: 450ms;
}

.translate-up {
    transform: translateY(-80px);
}

.slide:hover,
.slide.sortable-selected {
    .slide-preview,
    .slide-order,
    .slide-user {
        transform: translateY(0px);
        opacity: 1;
    }
}

.slide.show-upload-user {
    .slide-user {
        transform: translateY(0px);
        opacity: 1;
    }
}

.orange-label {
    color: $primary-orange;
    border-color: $primary-orange;
}

.deep-gray-label {
    color: $deep-gray;
    border-color: $deep-gray;
}

.slide-user {
    position: absolute;
    background: $light-gray;
    bottom: 10px;
    left: 10px;
    // width: 32px;
    overflow: hidden;
    min-width: 50px;
    max-width: 110px;
    border-radius: 20px;
    height: 32px;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    font-weight: bold;
    text-transform: capitalize;
    border: 2px solid;
    padding: 0 12px;

    font-size: 0.8rem;
    @include mobile() {
        max-width: min(110px, 57%);
    }

    .slide-user-inner {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        max-width: 100%;
        gap: 4px;

        span {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            margin: 0;
            margin-top: 2px;
        }
    }
}

.dupe-icon {
    font-size: 1rem;
    color: orange;
}

.slide-top-left {
    z-index: 2;
    top: 8px;
    left: 8px;
    position: absolute;
    display: flex;
    // flex-direction: column;
    flex-direction: row;
    gap: 4px; // align-items: flex-start;

    .duration {
        margin-left: 18px;
        text-wrap: nowrap;
        &:before {
            content: '';
            background: lighten($medium-gray, 16%);
            width: 2px;
            height: 10px;
            bottom: 0;
            position: absolute;
            top: 0;
            margin: auto;
            border-radius: 4px;
            transform: translateX(-10px);
        }
    }
}

.slide-top-right {
    z-index: 2;
    top: 8px;
    right: 8px;
    position: absolute;
    display: flex;
    // flex-direction: column;
    flex-direction: row;
    gap: 4px; // align-items: flex-start;
}

.slide.read-only {
    .duration {
        margin-left: 0;
        &:before {
            display: none;
        }
    }
}
.group-highlight {
    .slide-image {
        border: 8px solid orange !important;
    }
}

.selected-indicator {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    background: #ea580c;
    border: 1.5px solid #ea580c;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ghost-hidden {
    display: none !important;
}

.ghost-slide {
    // width: 242px;
    // height: 242px;
    height: 100%;
    max-height: 242px;
    min-height: 100px;
    aspect-ratio: 1;
    border: 1px dashed #d1d5db;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    flex-shrink: 0;
}

.ghost-icon-container {
    width: 52px;
    height: 38px;
    background: #fff7ed;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    position: absolute;
    margin: auto;
    inset: 0;
    max-width: var(--max-slide-width);
    max-height: var(--max-slide-width);
}

.ghost-icon {
    width: 20px;
    height: 20px;
}

.center-delete-btn {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    background-color: $medium-red;
    color: white;
    aspect-ratio: 1;
    border-radius: 50%;
    max-width: 30%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    opacity: 0;
    transition: 0.3s;
}

.center-slide-overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    color: white;
    aspect-ratio: 1;
    border-radius: 50%;
    max-width: 30%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
}

.slide:hover .center-delete-btn {
    opacity: 1;
}

.modal-thumb-grid {
    margin-top: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    max-height: 300px;
    overflow: auto;
    .thumb {
        position: relative;
        .slide-order {
            position: absolute;
            inset: 10px;
        }
    }
    .slide-image {
        border-radius: 8px;
        height: 70px;
        width: 70px;
        object-fit: cover;
    }

    &.single {
        .thumb {
            .slide-order {
                width: fit-content;
            }
            .slide-image {
                width: 200px;
                height: 200px;
            }
        }
    }
}

.preview-loading-indicator {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;

    display: flex;
    align-items: center;
    justify-content: center;
}

.cursor-disallowed {
    cursor: not-allowed !important;
}

.v-carousel .edit-button {
    position: absolute;
    left: auto;
    right: 10px;
    top: 10px;
    margin: auto;
    z-index: 99;
    width: fit-content;

    .v-btn:not(.v-btn--round).v-size--default {
        padding: 0;
        min-width: 36px;
        width: 48px;
    }
}
.slide-delete-modal {
    &::v-deep {
        .modal-header {
            justify-content: start;
            margin-bottom: 0;
            .icon {
                height: 32px;
                margin-right: 4px;
            }
        }
        .modal-header,
        .modal-content,
        .modal-footer {
            border: none;
        }
    }
}
.image-error-div {
    min-height: 90%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}
.v-icon.custom-duration {
    font-size: 1rem;
    transform: rotate(-90deg);
}
</style>
